import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, useTheme, alpha, TableSortLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Select, MenuItem, FormControl, InputLabel,
  Snackbar,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Send as DeliverIcon,
  Delete as DeleteIcon,
  RestartAlt as RestartIcon,
  Unarchive as UnarchiveIcon,
  Inventory2 as ArchiveIcon,
  Clear as ClearIcon,
  ContentCopy as CopyIcon,
  Email as ResendIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Insights as AnalyticsIcon,
  DownloadDone as DownloadOnIcon,
  DoNotDisturb as DownloadOffIcon,
  Visibility as PreviewIcon,
  Comment as CommentIcon,
  CommentsDisabled as CommentOffIcon,
  CameraAlt as CameraIcon,
  NoPhotography as CameraOffIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  AdjustOutlined as PartialIcon,
  Gavel as NdaOnIcon,
  GavelOutlined as NdaOffIcon,
} from '@mui/icons-material';
import { Switch, FormControlLabel, TextField, Badge, CircularProgress, Checkbox, Stack } from '@mui/material';
import { Download as DownloadFilledIcon } from '@mui/icons-material';
import { Collapse, Drawer, Divider } from '@mui/material';
// recharts imports retained for future analytics charts
import { fetchJobs, deleteJob, restartJob, updateJobDownload, restoreJob } from '../store/slices/jobsSlice';
import { fetchPackages, revokeShare } from '../store/slices/deliverySlice';
import api from '../services/api';
import { CANONICAL_ORIGIN } from '../config';
import DeliverDialog from '../components/DeliverDialog';
import ActiveTransfersPanel from '../components/ActiveTransfersPanel';
import useSortableTable from '../utils/useSortableTable';
import { fmtDateTime, fmtDate } from '../utils/formatDate';
import {
  computeLibraryAggregates,
  formatRelativeTime,
  shouldPulse,
  isStalled,
  shouldShowLibraryBulkOps,
  isLibraryBulkOpsForced,
  setLibraryBulkOpsForced,
  formatWatchPercent,
} from '../utils/library';
import { useBulkRunner } from '../utils/bulkOps';
import BulkActionBar from '../components/BulkActionBar';

// Color map for the unified lifecycle chip on Library rows. The chip
// absorbs encode + HLS + delivery + recipient activity, so we want
// visual contrast between "still working", "awaiting recipient",
// "active right now", and "fully through".
const LIFECYCLE_CHIP = {
  encoding:        { color: 'warning', label: 'Encoding' },
  stream_queued:   { color: 'warning', label: 'Stream queued' },
  stream_starting: { color: 'warning', label: 'Stream starting' },
  stream_encoding: { color: 'warning', label: 'Stream encoding' },
  ready:           { color: 'info',    label: 'Ready' },
  sent:            { color: 'default', label: 'Sent' },
  opened:          { color: 'default', label: 'Opened' },
  nda_signed:      { color: 'default', label: 'NDA signed' },
  downloading:     { color: 'info',    label: 'Downloading' },
  watched_partial: { color: 'info',    label: 'Started watching' },
  watching:        { color: 'info',    label: 'Watching' },
  downloaded:      { color: 'success', label: 'Downloaded' },
  watched:         { color: 'success', label: 'Watched' },
  failed:          { color: 'error',   label: 'Failed' },
  cancelled:       { color: 'default', label: 'Cancelled' },
};

// Adds the encode/HLS progress percent to the chip label when applicable
// so a user can see "Encoding 35%" instead of just "Encoding".
function lifecycleLabel(job) {
  const stage = job.lifecycle_stage || job.status;
  const cfg = LIFECYCLE_CHIP[stage] || { color: 'default', label: job.lifecycle_label || stage };
  let label = cfg.label;
  if (stage === 'encoding' && typeof job.progress === 'number' && job.progress > 0) {
    label = `Encoding ${Math.round(job.progress)}%`;
  } else if (stage === 'stream_encoding' && typeof job.hls_progress === 'number') {
    label = `Stream encoding ${Math.round(job.hls_progress)}%`;
  } else if (stage === 'stream_queued' && job.hls_queue_position) {
    label = `Stream queued (#${job.hls_queue_position})`;
  } else if (stage === 'watched_partial' && job.lifecycle_label) {
    // Backend buckets the partial-watch percent ("Watched <25%" etc.) —
    // surface that instead of the generic stage label.
    label = job.lifecycle_label;
  } else if (stage === 'watching' && typeof job.watch_max_percent === 'number') {
    // Active session: show the running percent so the operator can see
    // progress while it's happening.
    label = `Watching ${Math.round(job.watch_max_percent)}%`;
  }
  return { ...cfg, label, stage };
}

// Lifecycle status indicator — leading colored dot + label text. Avoids
// the ragged-edge problem of width-varying chip boxes by anchoring the
// visual element (the dot) at a fixed position; text flows after it.
// Active/in-flight stages pulse so the operator can scan a queue and
// spot what's still working.
const LIFECYCLE_DOT_COLOR = {
  default: 'rgba(255,255,255,0.45)',
  warning: '#d4a017',
  info:    '#42a5f5',
  success: '#66bb6a',
  error:   '#ef5350',
};
const LIFECYCLE_TEXT_COLOR = {
  default: 'text.secondary',
  warning: 'warning.main',
  info:    'info.main',
  success: 'success.main',
  error:   'error.main',
};
// v10.21.3: link state is independent of recipient activity. The
// chip color/label map mirrors backend api/jobs.py:link_state values.
//   pending  — no shares yet (encoded but never delivered)
//   active   — at least one share works
//   expired  — every share's window has closed
//   revoked  — every share has been revoked
const LINK_STATE_CHIP = {
  pending:  { color: 'default', label: 'Not delivered' },
  active:   { color: 'success', label: 'Active' },
  expired:  { color: 'warning', label: 'Expired' },
  revoked:  { color: 'default', label: 'Revoked' },
};

function LifecycleStatus({ job }) {
  const cfg = lifecycleLabel(job);
  const dotColor = LIFECYCLE_DOT_COLOR[cfg.color] || LIFECYCLE_DOT_COLOR.default;
  const textColor = LIFECYCLE_TEXT_COLOR[cfg.color] || LIFECYCLE_TEXT_COLOR.default;
  // Pulse only on encoding-side stages (encode + HLS-encode + HLS-start).
  // Recipient-side activity (watching, downloading) used to pulse too,
  // which turned a busy library into a Christmas tree of animations.
  // See utils/library.js#PULSING_STAGES.
  const isActive = shouldPulse(cfg.stage);

  // v10.21.3: two-chip render. Status chip (link state) sits left of
  // the activity dot+label. Skip the status chip for terminal failures
  // (failed/cancelled) and for pre-delivery stages (encoding/stream_*/
  // ready) where the link concept doesn't apply yet — link_state is
  // 'pending' but rendering it next to "Encoding 35%" is noise.
  // Filed against the v10.21.x review: "Expired removes the ability
  // to quickly see how much if any was watched." The fix is to never
  // collapse the two facts into one label again.
  const linkState = job.link_state;
  const showStatusChip = (
    linkState
    && linkState !== 'pending'
    && !['encoding', 'stream_queued', 'stream_starting', 'stream_encoding', 'ready', 'failed', 'cancelled'].includes(cfg.stage)
  );
  const statusCfg = showStatusChip ? LINK_STATE_CHIP[linkState] : null;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0, flexWrap: 'wrap' }}>
      {statusCfg && (
        <Chip
          size="small"
          label={statusCfg.label}
          color={statusCfg.color}
          variant="outlined"
          sx={{ height: 18, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
        />
      )}
      <Box
        component="span"
        sx={{
          width: 8, height: 8, borderRadius: '50%',
          bgcolor: dotColor, flexShrink: 0,
          boxShadow: isActive ? `0 0 0 0 ${dotColor}` : 'none',
          animation: isActive ? 'lifecycle-pulse 1.6s ease-in-out infinite' : 'none',
          '@keyframes lifecycle-pulse': {
            '0%':   { boxShadow: `0 0 0 0 ${dotColor}66` },
            '70%':  { boxShadow: `0 0 0 6px ${dotColor}00` },
            '100%': { boxShadow: `0 0 0 0 ${dotColor}00` },
          },
        }}
      />
      <Typography
        variant="body2"
        sx={{
          color: textColor,
          fontWeight: cfg.color === 'default' ? 400 : 500,
          fontSize: '0.82rem',
          lineHeight: 1.3,
          whiteSpace: 'nowrap',
        }}
      >
        {cfg.label}
      </Typography>
      {/* v10.21.34: distinct watch sittings rolled up to the job.
          One watch = default assumption, so we only render when the
          recipient genuinely re-watched (count >= 2). Hover for the
          tooltip explaining what "distinct" means — important so an
          operator doesn't mistake 11 for "11 sessions" (session count
          inflates on every player-tab reload). */}
      {typeof job.watch_distinct_count_max === 'number' && job.watch_distinct_count_max >= 2 && (
        <Tooltip title="Distinct watch sittings (resumes that pick up where they left off do not count as a new watch)">
          <Chip
            size="small"
            label={`Watched ${job.watch_distinct_count_max}×`}
            color="info"
            variant="outlined"
            sx={{ height: 18, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
          />
        </Tooltip>
      )}
    </Box>
  );
}

function fmtSize(bytes) {
  if (!bytes) return '—';
  const gb = bytes / (1024 ** 3);
  return gb >= 1 ? `${gb.toFixed(1)} GB` : `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
}

// Format a UTC ISO timestamp into the local-datetime <input> form, "YYYY-MM-DDTHH:mm",
// so the picker's value reflects the operator's wall clock instead of UTC.
function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// (Removed in v10.4.13: parked HLS_CHIP map and HlsChip helper —
// the unified LifecycleStatus already absorbs HLS state, so the
// dedicated chip was dead. Surfaces in git history if anyone needs
// to bring it back.)

// Small descriptor for the row's download button tooltip / chip.
function downloadStatusLabel(job) {
  if (!job?.allow_download) return 'Download: off (watch-only)';
  const now = Date.now();
  const from = job.download_enabled_from ? new Date(job.download_enabled_from).getTime() : null;
  const until = job.download_enabled_until ? new Date(job.download_enabled_until).getTime() : null;
  if (from && now < from) return `Download: opens ${fmtDateTime(job.download_enabled_from)}`;
  if (until && now > until) return `Download: closed ${fmtDateTime(job.download_enabled_until)}`;
  if (from || until) {
    return `Download: open${until ? ` until ${fmtDateTime(job.download_enabled_until)}` : ''}`;
  }
  return 'Download: on (always)';
}

// Resolve which "prep" phase a downloadable job is in, and how full the
// progress ring should render. The pipeline has three discrete phases
// after the Job hits COMPLETED with allow_download=true:
//
//   1. Canonical MP4 derivation (Job COMPLETED, no Package row yet)
//      — copy the top HLS rung to a canonical .mp4 on NAS.
//   2. MinIO sync (Package exists, synced=false) — push to delivery
//      node. This is the long pole; can take minutes-to-hours on
//      large files.
//   3. Hash verification (synced=true, hash_status != 'done') — sha256
//      + md5 for the recipient-facing integrity display. Doesn't block
//      download — recipients can grab the file as soon as it's synced —
//      but the operator sees a subtle "verifying" hint while it runs.
//
// We don't have byte-level granular progress from MinIO sync, so the
// ring uses three discrete steps (33/66/100) that map to the three
// phases. Better than a fake-precision percentage.
function getDownloadPrepState(job, pkg) {
  if (!job?.allow_download) return { phase: 'off' };
  if (pkg?.revoked) return { phase: 'revoked' };
  if (!pkg) return { phase: 'preparing', progress: 33, label: 'Generating download MP4…' };
  if (!pkg.synced) return { phase: 'uploading', progress: 66, label: 'Uploading to delivery node…' };
  if (pkg.hash_status && pkg.hash_status !== 'done') {
    // Synced — recipients CAN download — but hash is still cooking.
    // Treat as ready-with-suffix so the icon isn't grey/spinning.
    return { phase: 'verifying', progress: 100, label: 'Ready · verifying integrity (SHA256)…' };
  }
  return { phase: 'ready' };
}

// Horizontal delivery funnel — checkmark per stage. The count only
// appears when it adds information: a single recipient gets a clean
// checkmark with no "1/1" noise; a partially-progressed stage in a
// multi-recipient delivery shows "(2 of 3)". When all reach a stage,
// the connector to the next one fills in.
//
// Download/Complete are special:
// - When the job has download disabled (`allow_download === false`)
//   they're dropped entirely — a hollow circle for an unavailable
//   stage would read as a failure when it's actually fine.
// - When download IS enabled, they're optional from the recipient's
//   perspective (some screen, some download). We keep them in the
//   chain but tag with "(optional)" and use a subtler tone so an
//   unticked download doesn't look like an incomplete delivery.
function DeliveryFunnel({ funnel, allowDownload }) {
  const stages = [
    { key: 'sent',     label: 'Sent',          value: funnel.sent },
    { key: 'opened',   label: 'Opened',        value: funnel.opened },
    { key: 'nda',      label: 'NDA accepted',  value: funnel.nda },
  ];
  if (allowDownload) {
    stages.push({ key: 'started',  label: 'Download',  value: funnel.download_started,  optional: true });
    stages.push({ key: 'complete', label: 'Complete',  value: funnel.download_complete, optional: true });
  }
  const total = funnel.total || 0;

  const stageState = (val) => {
    if (total === 0) return 'none';
    if (val >= total) return 'all';
    if (val > 0)      return 'partial';
    return 'none';
  };

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0,
      mb: 2, px: 1.5, py: 1,
      border: 1, borderColor: 'divider', borderRadius: 1,
      bgcolor: 'background.default',
      flexWrap: 'wrap',
    }}>
      <Typography variant="caption" color="text.secondary" sx={{ mr: 1.5, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Delivery
      </Typography>
      {stages.map((s, i) => {
        const state = stageState(s.value);
        const Icon = state === 'all' ? CheckCircleIcon
                  : state === 'partial' ? PartialIcon
                  : UncheckedIcon;
        // Optional stages with no progress get even more muted so they
        // don't read as "delivery failed at this step." A reached
        // optional stage still goes solid gold — that's a real signal.
        const iconColor = state === 'all' ? '#d4a017'
                       : state === 'partial' ? '#d4a017'
                       : (s.optional ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.28)');
        const labelColor = state === 'none'
          ? (s.optional ? 'rgba(255,255,255,0.32)' : 'text.secondary')
          : 'text.primary';
        // Only show the count when it carries information:
        // - total > 1 AND partial progress (e.g. "2 of 3")
        // - 'all' with total > 1 isn't worth a count (the ✓ already says it)
        // - total === 1 never shows a count (the icon IS the verdict)
        const showCount = total > 1 && state === 'partial';
        const next = stages[i + 1];
        const connectorState = next
          ? (state === 'all' && stageState(next.value) !== 'none' ? 'full'
            : state === 'all' ? 'half'
            : state === 'partial' ? 'half'
            : 'none')
          : null;
        return (
          <React.Fragment key={s.key}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              <Icon sx={{ fontSize: 16, color: iconColor, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: labelColor, fontWeight: state === 'all' ? 600 : 500, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                {s.label}
                {s.optional && state === 'none' && (
                  <Box component="span" sx={{ color: 'rgba(255,255,255,0.28)', fontWeight: 400, ml: 0.5, fontStyle: 'italic' }}>
                    (optional)
                  </Box>
                )}
                {showCount && (
                  <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, ml: 0.5 }}>
                    ({s.value} of {total})
                  </Box>
                )}
              </Typography>
            </Box>
            {connectorState && (
              <Box sx={{
                flex: 1, minWidth: 12, height: 1.5, mx: 1,
                bgcolor: connectorState === 'full' ? '#d4a017'
                       : connectorState === 'half' ? 'rgba(212,160,23,0.35)'
                       : 'rgba(255,255,255,0.08)',
              }} />
            )}
          </React.Fragment>
        );
      })}
      {funnel.revoked > 0 && (
        <Chip size="small" color="error" label={`${funnel.revoked} revoked`} sx={{ ml: 1.5, height: 20, fontSize: '0.65rem' }} />
      )}
    </Box>
  );
}

// Where a recipient is in the delivery funnel — most-advanced step wins.
// Used both for the per-row chip and for the DCP card status. Ordering:
//   Revoked > Delivered (download complete) > Downloading > NDA accepted >
//   Opened > Sent > Created.
function lifecycleStage(lc) {
  if (!lc) return { key: 'created', label: 'Created', color: 'default' };
  if (lc.revoked_at)            return { key: 'revoked',     label: 'Revoked',     color: 'default' };
  if (lc.download_complete_at)  return { key: 'delivered',   label: 'Delivered',   color: 'success' };
  if (lc.download_started_at)   return { key: 'downloading', label: 'Downloading', color: 'info' };
  if (lc.nda_accepted_at)       return { key: 'nda',         label: 'NDA accepted', color: 'info' };
  if (lc.opened_at)             return { key: 'opened',      label: 'Opened',      color: 'info' };
  if (lc.email_sent_at)         return { key: 'sent',        label: 'Sent',        color: 'default' };
  return { key: 'created', label: 'Created', color: 'default' };
}

// Plain-English analytics content for the Library drawer.
// Backend computes a one-line `headline` and a list of bulleted
// `signal_lines` based on the per-session aggregates, so non-technical
// operators get a quick read like "3 viewers; 2 watched it through to
// the end." The raw numbers and per-session breakdown stay available
// behind a "Show details" toggle for debugging, and a "Show test plays"
// toggle re-fetches with ?include_test=true so operators can see their
// own preview sessions when needed.
function AnalyticsContent({ data, job, onReload }) {
  const [includeTest, setIncludeTest] = useState(false);

  const handleToggleTest = (e) => {
    const next = e.target.checked;
    setIncludeTest(next);
    onReload(job, { includeTest: next });
  };

  const flagSession = async (sessionId, asTest) => {
    try {
      await api.patch(`/api/jobs/${job.id}/sessions/${sessionId}/flag`, {
        is_operator_preview: asTest,
      });
      onReload(job, { includeTest });
    } catch (e) {
       
      alert(e.response?.data?.detail || `Could not update: ${e.message}`);
    }
  };

  const summary = data.summary || {};
  const viewers = data.viewers || [];
  const availableRungs = data.available_rungs || [];
  const totalSessions = viewers.reduce((n, v) => n + v.sessions.length, 0);

  // Behavior labels — what KIND of viewing happened, not just how far
  // they got. The backend computes these from real coverage (sum of
  // play intervals), region count (how scattered), and repeat ratio
  // (re-watch detection). Replaces the legacy bucket which used
  // furthest_percent and credited scrubs as if they were watched.
  const BEHAVIOR_LABEL = {
    finished:    { text: 'Watched it through',         color: 'success' },
    viewing:     { text: 'Watching it through',        color: 'success' },
    rewatching:  { text: 'Re-watched scenes',          color: 'warning' },
    spot_check:  { text: 'Spot-checked',               color: 'info' },
    sampled:     { text: 'Sampled, didn\'t get far',   color: 'default' },
    bounced:     { text: 'Quit immediately',           color: 'default' },
    not_played:  { text: 'Hasn\'t played yet',         color: 'default' },
  };
  // Legacy bucket kept for back-compat fallback when the response
  // predates the behavior classifier.
  const BUCKET_LABEL = {
    finished:     { text: 'Reached the end',         color: 'success' },
    watched_most: { text: 'Watched most of it',      color: 'warning' },
    sampled:      { text: 'Sampled, didn\'t get far', color: 'default' },
    bounced:      { text: 'Quit immediately',        color: 'default' },
  };
  // v10.19.2: stuck-on-low-quality verdict surfaces the chunkiness
  // case (Drew Pinsky's v10.19.1 fingerprint — locked to a single
  // sub-3-Mbps rung the whole session). Server provides a friendly
  // explanation in `playback_quality_explanation` which the chip
  // tooltip surfaces verbatim.
  const QUALITY_LABEL = {
    smooth:               null,
    occasional_buffering: 'Occasional buffering',
    rough:                'Rough playback (heavy buffering)',
    errors:               'Playback errors',
    low_quality_locked:   'Stuck on low quality',
  };

  const fmtAgo = (iso) => {
    if (!iso) return '';
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  // "Sat 1:24 PM" / "Apr 26, 1:24 PM" — readable for non-streaming
  // operators, still precise to the minute.
  const fmtClock = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    if (sameDay) return `Today ${time}`;
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  // "23m 14s" — precise but human-readable. Avoids "0 minutes" for very
  // short visits.
  const fmtDur = (totalSec) => {
    if (totalSec == null) return '—';
    const s = Math.max(0, Math.round(totalSec));
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const r = s % 60;
    if (m < 60) return r ? `${m}m ${r}s` : `${m}m`;
    const h = Math.floor(m / 60);
    const mr = m % 60;
    return mr ? `${h}h ${mr}m` : `${h}h`;
  };

  // Delivery lifecycle funnel — counted across every Share for this job
  // (including sessionless ones). Surfaced at the top of the drawer so
  // operators see "did the email land, did they open it, did they
  // download" without expanding any row. The same stage logic runs per
  // row below.
  const deliveryFunnel = (() => {
    const f = {
      total: viewers.length,
      sent: 0, opened: 0, nda: 0,
      download_started: 0, download_complete: 0, revoked: 0,
    };
    for (const v of viewers) {
      const lc = v.lifecycle || {};
      if (lc.revoked_at) f.revoked += 1;
      if (lc.email_sent_at) f.sent += 1;
      if (lc.opened_at) f.opened += 1;
      if (lc.nda_accepted_at) f.nda += 1;
      if (lc.download_started_at) f.download_started += 1;
      if (lc.download_complete_at) f.download_complete += 1;
    }
    return f;
  })();

  return (
    <>
      <Typography variant="body1" sx={{ fontWeight: 500, mb: 1.5, lineHeight: 1.5 }}>
        {data.headline}
      </Typography>
      {(data.signal_lines || []).length > 0 && (
        <Box component="ul" sx={{ pl: 2.5, mb: 2, '& li': { mb: 0.5 } }}>
          {(data.signal_lines || []).map((line, i) => (
            <li key={i}>
              <Typography variant="body2" color="text.secondary">{line}</Typography>
            </li>
          ))}
        </Box>
      )}

      {/* Delivery funnel — compact horizontal pipeline showing every
          stage in one slim row. The expandable Delivery cell on each
          recipient row gives full timeline detail. */}
      {viewers.length > 0 && (
        <DeliveryFunnel funnel={deliveryFunnel} allowDownload={!!job?.allow_download} />
      )}

      {summary.test_play_count > 0 && (
        <FormControlLabel
          control={<Switch size="small" checked={includeTest} onChange={handleToggleTest} />}
          label={<Typography variant="caption">Show {summary.test_play_count} operator test play{summary.test_play_count !== 1 ? 's' : ''}</Typography>}
          sx={{ mb: 1 }}
        />
      )}

      {availableRungs.length > 0 && (
        <Box sx={{ mt: 1, mb: 1, p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            HLS ladder built for this screener
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
            {availableRungs.map(r => `${r.name} (${r.resolution} @ ${r.bandwidth_kbps} kbps)`).join('  ·  ')}
          </Typography>
        </Box>
      )}

      {viewers.length > 0 && (
        <RecipientsTable
          viewers={viewers}
          totalSessions={totalSessions}
          BUCKET_LABEL={BUCKET_LABEL}
          BEHAVIOR_LABEL={BEHAVIOR_LABEL}
          QUALITY_LABEL={QUALITY_LABEL}
          fmtClock={fmtClock}
          fmtDur={fmtDur}
          fmtAgo={fmtAgo}
          flagSession={flagSession}
        />
      )}
    </>
  );
}

// One row per recipient. Click a row to expand its individual sessions
// inline as a sub-table. Goal is "everything operators want to see in one
// scan" without 50 stacked cards: each viewer collapses to a compact line
// with name/bucket/%/time/sessions/last-seen, plus expand-for-forensics.
function RecipientsTable({
  viewers, totalSessions, BUCKET_LABEL, BEHAVIOR_LABEL, QUALITY_LABEL,
  fmtClock, fmtDur, fmtAgo, flagSession,
}) {
  const [expanded, setExpanded] = useState({});  // share_id → bool
  const toggle = (id) => setExpanded((m) => ({ ...m, [id]: !m[id] }));

  // Aggregate counts for the totals bar — gives operators a one-line read
  // before they scan rows.
  const tot = viewers.reduce((acc, v) => {
    acc.finished += v.bucket === 'finished' ? 1 : 0;
    acc.rebufferEvents += v.rebuffer_count || 0;
    acc.netRebuffer += v.rebuffer_network || 0;
    acc.errors += v.playback_quality === 'errors' ? 1 : 0;
    return acc;
  }, { finished: 0, rebufferEvents: 0, netRebuffer: 0, errors: 0 });

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 3, mb: 1.5 }}>
        <Typography variant="subtitle2">Recipients</Typography>
        <Typography variant="caption" color="text.secondary">
          <strong>{viewers.length}</strong> recipient{viewers.length !== 1 ? 's' : ''}
          {' · '}
          <strong>{totalSessions}</strong> session{totalSessions !== 1 ? 's' : ''}
          {tot.finished > 0 && <> · <strong>{tot.finished}</strong> finished</>}
          {tot.netRebuffer > 0 && <> · <strong>{tot.netRebuffer}</strong> network rebuffer{tot.netRebuffer !== 1 ? 's' : ''}</>}
          {tot.errors > 0 && <> · <Typography component="span" variant="caption" color="error"><strong>{tot.errors}</strong> playback error{tot.errors !== 1 ? 's' : ''}</Typography></>}
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
        <Table size="small" sx={{
          '& td, & th': { borderBottom: '1px solid', borderColor: 'divider' },
          '& th': { fontWeight: 600, bgcolor: 'background.default' },
        }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 32, p: 0.5 }} />
              <TableCell>Recipient</TableCell>
              <TableCell sx={{ width: 130 }}>Delivery</TableCell>
              <TableCell sx={{ width: 160 }}>Behavior</TableCell>
              <TableCell align="right" sx={{ width: 110 }}>Watched</TableCell>
              <TableCell align="right" sx={{ width: 110 }}>Time on page</TableCell>
              <TableCell align="right" sx={{ width: 60 }}>Visits</TableCell>
              <TableCell sx={{ width: 160 }}>Last activity</TableCell>
              <TableCell sx={{ width: 130 }}>Quality</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {viewers.map((v) => {
              // Prefer the new behavior classifier; fall back to the
              // legacy bucket if the response is from a backend that
              // hasn't been redeployed yet.
              const behaviorCfg = (v.behavior && BEHAVIOR_LABEL[v.behavior])
                || BUCKET_LABEL[v.bucket]
                || { text: v.behavior_label || v.bucket, color: 'default' };
              const qLabel = QUALITY_LABEL[v.playback_quality];
              const isOpen = !!expanded[v.share_id];
              const watchedPct = Math.round(v.watched_percent ?? v.furthest_percent ?? 0);
              const reachedPct = Math.round(v.furthest_percent || 0);
              // Highlight when scrubbing went well past actually-watched
              // depth — that's the visible signal that they jumped
              // around without watching most of the gap.
              const scrubGap = reachedPct - watchedPct;
              const ipCount = (v.ip_addresses || []).length;
              const concurrentIpOverlaps = _findConcurrentIpOverlaps(v.sessions);
              const hasConcurrent = concurrentIpOverlaps.length > 0;
              const hasMultiIp = ipCount >= 2;
              const hasHistory = !!v.recipient_history;
              const qualityNode = (() => {
                // No sessions = no playback signal. "Smooth" was misleading
                // — there was nothing to be smooth about.
                if (v.behavior === 'not_played' || (v.session_count || 0) === 0) {
                  return <Typography variant="caption" color="text.secondary">—</Typography>;
                }
                // v10.19.2: wrap the verdict in a Tooltip so the operator
                // can hover for the user-friendly explanation. Server-side
                // classifier returns `playback_quality_explanation`.
                const explanation = v.playback_quality_explanation || '';
                const wrap = (node) => explanation ? (
                  <Tooltip title={explanation} placement="top" arrow>{node}</Tooltip>
                ) : node;
                if (v.playback_quality === 'errors') {
                  return wrap(<Typography variant="caption" color="error" sx={{ cursor: 'help' }}>Errors</Typography>);
                }
                if (v.playback_quality === 'low_quality_locked') {
                  return wrap(<Typography variant="caption" color="warning.main" sx={{ cursor: 'help' }}>Stuck on low quality</Typography>);
                }
                if (v.rebuffer_network > 0) {
                  // v10.21.47: network-induced rebuffers are a hard
                  // playback failure (the movie literally stopped
                  // playing for the recipient). Operator feedback:
                  // "if those gaps came from the movie buffering,
                  // they should show as red." Amber was confusing
                  // it with the lighter "stuck on low quality"
                  // signal, which is a separate concern.
                  return wrap(<Typography variant="caption" color="error" sx={{ cursor: 'help' }}>{v.rebuffer_network} net rebuffer{v.rebuffer_network !== 1 ? 's' : ''}</Typography>);
                }
                if (qLabel) {
                  return wrap(<Typography variant="caption" color="warning.main" sx={{ cursor: 'help' }}>{qLabel}</Typography>);
                }
                return wrap(<Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>Smooth</Typography>);
              })();
              // Last activity falls back to the most recent lifecycle
              // event when there are no watch sessions, so "Hasn't played
              // yet" rows still tell the operator when the recipient
              // last *did* something (open the link, accept the NDA, …).
              const lastActivity = (() => {
                if (v.last_seen) return v.last_seen;
                const lc = v.lifecycle || {};
                return lc.download_complete_at || lc.download_started_at || lc.nda_accepted_at
                    || lc.opened_at || lc.email_sent_at || lc.created_at || null;
              })();
              return (
                <React.Fragment key={v.share_id}>
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer', '& > *': { py: 1.25 } }}
                    onClick={() => toggle(v.share_id)}
                  >
                    <TableCell sx={{ p: 0.5 }}>
                      <IconButton size="small" tabIndex={-1} sx={{ p: 0.25 }}>
                        {isOpen ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight={500}>
                          {v.recipient_name || '—'}
                        </Typography>
                        {v.any_test && <Chip size="small" label="Test" sx={{ height: 16, fontSize: '0.6rem' }} />}
                        {/* Concurrent multi-IP is the loudest signal —
                            two locations watching at the same time is a
                            shared-link smoking gun. Multi-IP without
                            concurrency is just "they used 2 devices". */}
                        {hasConcurrent && (
                          <Tooltip title={`Watching from ${ipCount} different IPs at the same time — link likely shared`}>
                            <Chip size="small" color="error" label={`${ipCount} IPs concurrent`} sx={{ height: 18, fontSize: '0.65rem' }} />
                          </Tooltip>
                        )}
                        {hasMultiIp && !hasConcurrent && (
                          <Tooltip title={`Accessed from ${ipCount} different IPs across separate sessions`}>
                            <Chip size="small" color="warning" variant="outlined" label={`${ipCount} IPs`} sx={{ height: 18, fontSize: '0.65rem' }} />
                          </Tooltip>
                        )}
                        {hasHistory && (
                          <Tooltip title={`Known recipient — ${v.recipient_history.prior_share_count} prior share${v.recipient_history.prior_share_count !== 1 ? 's' : ''}`}>
                            <Chip size="small" color="info" variant="outlined" label="Known" sx={{ height: 18, fontSize: '0.65rem' }} />
                          </Tooltip>
                        )}
                      </Box>
                      {v.recipient_email && (
                        <Typography variant="caption" color="text.secondary" component="div">
                          {v.recipient_email}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const stage = lifecycleStage(v.lifecycle);
                        const sub = (() => {
                          const lc = v.lifecycle || {};
                          if (stage.key === 'delivered' && (lc.download_count || 0) > 1) {
                            return `${lc.download_count}×`;
                          }
                          if (stage.key === 'downloading') return 'in progress';
                          if (stage.key === 'opened' && !lc.nda_accepted_at) return 'no NDA yet';
                          if (stage.key === 'sent') return 'not opened';
                          if (stage.key === 'created') return 'not sent';
                          return null;
                        })();
                        return (
                          <Box>
                            <Chip size="small" label={stage.label} color={stage.color} variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                            {sub && (
                              <Typography variant="caption" color="text.secondary" component="div" sx={{ fontSize: '0.65rem', mt: 0.25 }}>
                                {sub}
                              </Typography>
                            )}
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={
                        v.behavior === 'rewatching' ? `Re-watched (repeat ratio ${v.repeat_ratio || 1.0}× — covered some sections more than once)`
                        : v.behavior === 'spot_check' ? `Spot-check: ${v.watched_region_count || 0} disjoint segments, ${v.seek_count || 0} seeks`
                        : v.behavior === 'viewing' ? 'Watched mostly contiguously'
                        : v.behavior === 'finished' ? 'Watched start to finish'
                        : v.behavior === 'sampled' ? 'Watched a small portion'
                        : v.behavior === 'bounced' ? 'Started the player but quit after watching <5%'
                        : v.behavior === 'not_played' ? 'Opened the share page but never started the player'
                        : behaviorCfg.text
                      }>
                        <Chip size="small" label={behaviorCfg.text} color={behaviorCfg.color} variant="outlined" />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={
                        scrubGap > 10
                          ? `${watchedPct}% of the picture actually played; max position reached ${reachedPct}% (scrubbed past ${scrubGap}%)`
                          : `${watchedPct}% of the picture actually played${reachedPct ? ` (max position ${reachedPct}%)` : ''}`
                      }>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            color={watchedPct >= 95 ? 'success.main' : watchedPct >= 50 ? 'warning.main' : 'text.primary'}
                          >
                            {watchedPct}%
                          </Typography>
                          {scrubGap > 10 && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                              scrubbed to {reachedPct}%
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{fmtDur(v.total_session_seconds)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{v.session_count}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" component="div">{fmtClock(lastActivity)}</Typography>
                      <Typography variant="caption" color="text.secondary" component="div">
                        {fmtAgo(lastActivity)}
                      </Typography>
                    </TableCell>
                    <TableCell>{qualityNode}</TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ bgcolor: 'background.default', py: 2, px: 3 }}>
                        <ViewerDetail
                          viewer={v}
                          fmtClock={fmtClock}
                          fmtDur={fmtDur}
                          flagSession={flagSession}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

// Identify pairs of sessions for the same recipient that came from
// different IPs at OVERLAPPING wall-clock windows. Two sessions overlap
// when (a.start < b.end) AND (b.start < a.end), so we get one entry per
// concurrent pair. Returns [{ipA, ipB, overlap_seconds, started_at}, …].
function _findConcurrentIpOverlaps(sessions) {
  const items = (sessions || [])
    // Operator preview sessions (spectator "follow viewer" or other op_token
    // joins) come from a different IP than the recipient by design — that's
    // not a shared-link signal, that's the operator joining their own
    // session. Exclude them even when "Show test plays" is on so the
    // shared-link alarm doesn't false-positive on the operator's own use.
    .filter((s) => !s.is_operator_preview)
    .map((s) => {
      const start = s.started_at ? new Date(s.started_at).getTime() : null;
      const end = (s.ended_at || s.last_event_at)
        ? new Date(s.ended_at || s.last_event_at).getTime() : null;
      return start && end && s.ip_address ? { ip: s.ip_address, start, end } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start);
  const overlaps = [];
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const a = items[i];
      const b = items[j];
      if (a.ip === b.ip) continue;          // same IP isn't suspicious
      if (b.start >= a.end) break;          // sorted, nothing else can overlap
      const overlapStart = Math.max(a.start, b.start);
      const overlapEnd = Math.min(a.end, b.end);
      const overlapSec = Math.max(0, Math.round((overlapEnd - overlapStart) / 1000));
      overlaps.push({
        ipA: a.ip,
        ipB: b.ip,
        overlap_seconds: overlapSec,
        started_at: new Date(overlapStart).toISOString(),
      });
    }
  }
  return overlaps;
}

// Squeeze a User-Agent string down to "browser / OS" for the sessions
// table. Operators don't care that it's "Mozilla/5.0 (Windows NT 10.0;
// Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0
// Safari/537.36" — they care that it was Chrome on Windows.
function _parseUserAgent(ua) {
  if (!ua) return null;
  let browser = null;
  let os = null;
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Version\/[\d.]+ Safari/.test(ua)) browser = 'Safari';
  else if (/Chromium\//.test(ua)) browser = 'Chromium';
  if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Windows NT/.test(ua)) os = 'Windows';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/Linux/.test(ua)) os = 'Linux';
  if (!browser && !os) return null;
  return [browser, os].filter(Boolean).join(' / ');
}

// Inline drill-down for a single recipient. Two parts:
//   (1) recipient-wide signals — IPs, ABR ladder hit, scrub/pause counts,
//       rebuffer breakdown by cause. These are sums across all sessions.
//   (2) one row per session in a tight sub-table with the forensic facts.
// Reusable delivery-lifecycle timeline. Used by both the watermark
// analytics drawer (per recipient) and the DCP analytics drawer
// (different product, same delivery model). Steps that don't apply to
// a given product are silently skipped (e.g. NDA on DCP shares).
//
// Each step renders as a row: ✓ icon when reached / · when not yet,
// label, absolute timestamp, relative-time hint, optional sublabel.
// Operator can scan top-to-bottom and see exactly where a delivery
// stalled.
export function DeliveryLifecycleTimeline({ lifecycle, includeNda = true, fmtClock }) {
  if (!lifecycle) return null;

  const steps = [];
  steps.push({
    key: 'created',
    label: 'Link created',
    ts: lifecycle.created_at,
    required: true,
  });
  steps.push({
    key: 'emailed',
    label: 'Email sent',
    ts: lifecycle.email_sent_at,
    pendingHint: 'Email not yet sent',
  });
  steps.push({
    key: 'opened',
    label: 'Recipient opened the link',
    ts: lifecycle.opened_at,
    pendingHint: 'Not opened yet',
  });
  if (includeNda) {
    steps.push({
      key: 'nda',
      label: 'NDA accepted',
      ts: lifecycle.nda_accepted_at,
      pendingHint: 'NDA not yet accepted',
    });
  }
  steps.push({
    key: 'dl_started',
    label: 'Download started',
    ts: lifecycle.download_started_at,
    pendingHint: 'Recipient hasn\'t clicked Download',
  });
  steps.push({
    key: 'dl_complete',
    label: 'Download complete',
    sublabel: lifecycle.download_count > 0
      ? `${lifecycle.download_count} download attempt${lifecycle.download_count === 1 ? '' : 's'} on file`
      : null,
    ts: lifecycle.download_complete_at,
    pendingHint: 'No verified completion yet',
  });

  // Footer state — expires / revoked. Only render when present and
  // operator-relevant.
  const tail = [];
  if (lifecycle.revoked_at) {
    tail.push({ key: 'revoked', label: 'Link revoked', ts: lifecycle.revoked_at, color: 'error' });
  } else if (lifecycle.expires_at) {
    const ms = new Date(lifecycle.expires_at).getTime() - Date.now();
    const expired = ms < 0;
    const soon = !expired && ms <= 48 * 36e5;
    tail.push({
      key: 'expires',
      label: expired ? 'Link expired' : (soon ? 'Link expiring soon' : 'Link expires'),
      ts: lifecycle.expires_at,
      color: expired ? 'error' : soon ? 'warning' : 'text.secondary',
    });
  }

  const fmtAgo = (iso) => {
    if (!iso) return '';
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 0) {
      const a = Math.abs(ms);
      const d = Math.floor(a / 86400000);
      if (d > 0) return `in ${d}d`;
      const h = Math.floor(a / 3600000);
      return h > 0 ? `in ${h}h` : 'soon';
    }
    const m = Math.floor(ms / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.65rem', mb: 0.75 }}>
        Delivery lifecycle
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 0.5 }}>
        {steps.map((step) => {
          const reached = !!step.ts;
          return (
            <Box key={step.key} sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Box sx={{
                width: 14, textAlign: 'center', fontSize: '0.85rem',
                color: reached ? 'success.main' : 'text.disabled',
                lineHeight: 1,
              }}>{reached ? '✓' : '○'}</Box>
              <Typography variant="caption" sx={{ minWidth: 200, fontWeight: reached ? 500 : 400, color: reached ? 'text.primary' : 'text.secondary' }}>
                {step.label}
              </Typography>
              <Typography variant="caption" sx={{ flex: 1, color: reached ? 'text.primary' : 'text.disabled' }}>
                {reached ? `${fmtClock(step.ts)} · ${fmtAgo(step.ts)}` : step.pendingHint || '—'}
                {step.sublabel && reached && (
                  <Box component="span" sx={{ ml: 1, color: 'text.secondary' }}>· {step.sublabel}</Box>
                )}
              </Typography>
            </Box>
          );
        })}
        {tail.map((t) => (
          <Box key={t.key} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
            <Box sx={{ width: 14, textAlign: 'center', fontSize: '0.85rem', color: t.color || 'text.secondary', lineHeight: 1 }}>!</Box>
            <Typography variant="caption" sx={{ minWidth: 200, fontWeight: 600, color: t.color || 'text.primary' }}>
              {t.label}
            </Typography>
            <Typography variant="caption" sx={{ flex: 1, color: t.color || 'text.primary' }}>
              {fmtClock(t.ts)} · {fmtAgo(t.ts)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ViewerDetail({ viewer: v, fmtClock, fmtDur, flagSession }) {
  const allSessions = (v.sessions || []).slice().sort((a, b) =>
    (a.started_at < b.started_at ? 1 : -1)
  );
  // v10.19.4: operator-preview sessions carry the operator's own LAN
  // IP (192.168.x, 10.x, etc.) and would otherwise show up as "local
  // testing addresses" in the per-session table. Hide them by default;
  // the operator can flip the toggle if they're auditing a misflagged
  // preview. The Mark/Unmark-test button still works either way.
  const [showTests, setShowTests] = useState(false);
  const testSessionCount = allSessions.filter((s) => s.is_operator_preview).length;
  const sessions = showTests
    ? allSessions
    : allSessions.filter((s) => !s.is_operator_preview);
  const ips = v.ip_addresses || [];
  const qualities = v.quality_levels_seen || [];
  const concurrentIpOverlaps = _findConcurrentIpOverlaps(v.sessions);
  const history = v.recipient_history;

  // Distinct browser/OS pairs across this recipient's sessions — useful
  // forensic signal that's been collected forever but never surfaced.
  // Restricted to the non-preview set; the operator's own browser
  // would otherwise clutter the recipient's device list.
  const devices = Array.from(new Set(
    sessions.map((s) => _parseUserAgent(s.user_agent)).filter(Boolean)
  ));

  // Build the recipient-wide signals as a flat list of (label, value)
  // pairs and lay them out in a grid. Cleaner than 5 stacked captions.
  const signals = [];
  if (ips.length) signals.push(['Connected from', ips.join(', '), { mono: true }]);
  if (devices.length) signals.push(['Devices', devices.join(', ')]);
  if (qualities.length) {
    signals.push([
      'ABR ladder hit',
      qualities.join(' · ') + (v.level_switch_count > 0 ? ` (${v.level_switch_count} auto-adjustment${v.level_switch_count !== 1 ? 's' : ''})` : ''),
    ]);
  }
  if (v.seek_count > 0 || v.user_pause_count > 0) {
    // v10.19.2: surface "arrow-key skim" pattern. Forward hops of
    // 4–20 s are how HTML5 video reports right-arrow taps; many of
    // those in a row means the viewer was scrubbing through content,
    // not having playback issues. Operator's mental model is "what
    // were they DOING."
    const skim = v.forward_skim_count || 0;
    const jumps = v.forward_jump_count || 0;
    const backs = v.backward_seek_count || 0;
    const parts = [];
    if (v.seek_count > 0) parts.push(`${v.seek_count} scrub${v.seek_count !== 1 ? 's' : ''} total`);
    if (skim >= 3 && skim >= jumps && skim >= backs) {
      parts.push(`${skim} arrow-key skip${skim !== 1 ? 's' : ''} forward`);
    }
    if (jumps > 0) parts.push(`${jumps} big jump${jumps !== 1 ? 's' : ''} forward`);
    if (backs > 0) parts.push(`${backs} rewind${backs !== 1 ? 's' : ''}`);
    if (v.user_pause_count > 0) parts.push(`${v.user_pause_count} manual pause${v.user_pause_count !== 1 ? 's' : ''}`);
    signals.push(['Interaction', parts.join(' · ')]);
  }
  if (v.rebuffer_count > 0) {
    const parts = [
      `${v.rebuffer_network} from network`,
      v.rebuffer_seek_induced > 0 && `${v.rebuffer_seek_induced} after scrubs`,
      v.rebuffer_play_induced > 0 && `${v.rebuffer_play_induced} after manual play`,
    ].filter(Boolean);
    signals.push([
      'Rebuffering',
      `${parts.join(', ')} · ${fmtDur(v.rebuffer_seconds)} total`,
      // v10.21.47: any network-induced rebuffer = red. The
      // recipient saw the movie freeze. Amber was conflating
      // it with the lighter "stuck on low quality" signal.
      { color: v.playback_quality === 'errors' || v.rebuffer_network > 0 ? 'error' : 'text.secondary' },
    ]);
  }
  signals.push(['First opened', fmtClock(v.first_seen)]);

  return (
    <Box>
      {concurrentIpOverlaps.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            Concurrent access from different IPs
          </Typography>
          <Typography variant="caption" component="div">
            {concurrentIpOverlaps.slice(0, 5).map((o, i) => (
              <Box key={i} component="span" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.72rem' }}>
                {o.ipA} ↔ {o.ipB} — overlapped {fmtDur(o.overlap_seconds)} starting {fmtClock(o.started_at)}
              </Box>
            ))}
            {concurrentIpOverlaps.length > 5 && <span>… +{concurrentIpOverlaps.length - 5} more</span>}
          </Typography>
        </Alert>
      )}
      {history && (
        <Box sx={{
          mb: 2, p: 1.5,
          border: 1, borderColor: 'info.main', borderRadius: 1,
          bgcolor: alpha => alpha,
        }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, color: 'info.main' }}>
            Known recipient
          </Typography>
          <Box sx={{
            display: 'grid', gridTemplateColumns: 'max-content 1fr',
            columnGap: 2.5, rowGap: 0.5,
          }}>
            <Typography variant="caption" color="text.secondary">First seen</Typography>
            <Typography variant="caption">{fmtClock(history.first_seen)}</Typography>
            <Typography variant="caption" color="text.secondary">Prior screeners</Typography>
            <Typography variant="caption">
              {history.prior_job_count} job{history.prior_job_count !== 1 ? 's' : ''} · {history.prior_share_count} share{history.prior_share_count !== 1 ? 's' : ''} · {history.prior_session_count} session{history.prior_session_count !== 1 ? 's' : ''}
              {history.prior_download_count > 0 && ` · ${history.prior_download_count} download${history.prior_download_count !== 1 ? 's' : ''}`}
            </Typography>
            {history.prior_ips.length > 0 && <>
              <Typography variant="caption" color="text.secondary">Past IPs</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
                {history.prior_ips.join(', ')}
              </Typography>
            </>}
            {history.prior_devices.length > 0 && <>
              <Typography variant="caption" color="text.secondary">Past devices</Typography>
              <Typography variant="caption">
                {Array.from(new Set(history.prior_devices.map(_parseUserAgent).filter(Boolean))).join(', ') || '—'}
              </Typography>
            </>}
          </Box>
        </Box>
      )}
      {/* Delivery lifecycle — surfaces email/NDA/download facts the
          old analytics view didn't. Operator can see "did the email
          land, did they accept the NDA, did they actually finish the
          download" without leaving the drawer. */}
      <DeliveryLifecycleTimeline lifecycle={v.lifecycle} fmtClock={fmtClock} />

      {signals.length > 0 && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          columnGap: 2.5,
          rowGap: 0.75,
          mb: 2,
        }}>
          {signals.map(([label, value, opts], i) => (
            <React.Fragment key={i}>
              <Typography variant="caption" color="text.secondary" sx={{ pt: 0.25 }}>
                {label}
              </Typography>
              <Typography
                variant="caption"
                color={opts?.color || 'text.primary'}
                sx={{ fontFamily: opts?.mono ? 'monospace' : undefined, fontSize: opts?.mono ? '0.72rem' : undefined }}
              >
                {value}
              </Typography>
            </React.Fragment>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography variant="caption" color="text.secondary">
          Sessions ({sessions.length}
          {testSessionCount > 0 && !showTests && ` · ${testSessionCount} operator test hidden`})
        </Typography>
        {testSessionCount > 0 && (
          <FormControlLabel
            sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.7rem' } }}
            control={
              <Switch
                size="small"
                checked={showTests}
                onChange={(_e, v) => setShowTests(v)}
              />
            }
            label={`Show operator test sessions (${testSessionCount})`}
          />
        )}
      </Box>
      <TableContainer>
        <Table size="small" sx={{
          '& td, & th': { borderBottom: '1px solid', borderColor: 'divider', py: 0.75, px: 1 },
          '& th': { fontWeight: 600, fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 },
        }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 130 }}>Started</TableCell>
              <TableCell sx={{ width: 130 }}>Stopped</TableCell>
              <TableCell align="right" sx={{ width: 80 }}>Duration</TableCell>
              <TableCell align="right" sx={{ width: 70 }}>Reached</TableCell>
              <TableCell sx={{ width: 110 }}>IP</TableCell>
              <TableCell sx={{ width: 130 }}>Device</TableCell>
              <TableCell align="right" sx={{ width: 80 }}>Avg Mbps</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right" sx={{ width: 100 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map((s) => {
              // v10.5.5: use watched_percent (coverage of real playback
              // time) not furthest_percent (max scrub position). A
              // recipient who scrubbed to the end without watching no
              // longer reads as "99%". furthest_percent stays available
              // as a tooltip detail for forensic depth.
              const pct = Math.round(s.watched_percent ?? 0);
              const furthest = Math.round(s.furthest_percent ?? 0);
              const bucketLabel = formatWatchPercent(pct);
              const stopIso = s.ended_at || s.last_event_at;
              const device = _parseUserAgent(s.user_agent);
              const avgMbps = s.avg_bitrate_bps ? (s.avg_bitrate_bps / 1_000_000).toFixed(1) : null;
              const notes = [];
              if (!s.ended_at) notes.push('tab closed without notifying');
              if (s.error_count > 0) notes.push(`${s.error_count} error${s.error_count !== 1 ? 's' : ''}`);
              // Rebuffer breakdown by cause — exposes the same fact the
              // recipient-wide signals show, but per-session, so an
              // operator can see "this one visit had 12 net rebuffers"
              // even when other visits were clean.
              if (s.rebuffer_count > 0) {
                const parts = [
                  `${s.rebuffer_network || 0} net`,
                  s.rebuffer_seek_induced > 0 && `${s.rebuffer_seek_induced} scrub`,
                  s.rebuffer_play_induced > 0 && `${s.rebuffer_play_induced} play`,
                ].filter(Boolean);
                notes.push(`${parts.join('+')} rebuf${s.rebuffer_seconds > 0 ? ` (${fmtDur(s.rebuffer_seconds)})` : ''}`);
              }
              if (s.seek_count > 0) notes.push(`${s.seek_count} scrub${s.seek_count !== 1 ? 's' : ''}`);
              if (s.user_pause_count > 0) notes.push(`${s.user_pause_count} pause${s.user_pause_count !== 1 ? 's' : ''}`);
              if ((s.quality_levels_seen || []).length) {
                const ladder = s.quality_levels_seen.join('→');
                notes.push(s.level_switch_count > 0 ? `${ladder} (${s.level_switch_count}×)` : ladder);
              }
              return (
                <TableRow key={s.id} hover>
                  <TableCell><Typography variant="caption">{fmtClock(s.started_at)}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{fmtClock(stopIso)}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption">{fmtDur(s.session_duration_seconds)}</Typography></TableCell>
                  <TableCell align="right">
                    <Tooltip
                      arrow
                      title={`Watched ${pct}% (actual playback coverage)${furthest !== pct ? ` · scrubbed to ${furthest}%` : ''}`}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={500}
                        color={pct >= 90 ? 'success.main' : pct >= 50 ? 'warning.main' : 'text.primary'}
                      >
                        {bucketLabel}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{s.ip_address || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{device || '—'}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="caption">{avgMbps ? `${avgMbps}` : '—'}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="caption" color={s.error_count > 0 ? 'error' : 'text.secondary'}>
                      {notes.join(' · ') || '—'}
                    </Typography>
                    {s.is_operator_preview && (
                      <Chip size="small" label="Test" sx={{ ml: 0.5, height: 14, fontSize: '0.55rem' }} />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="text"
                      sx={{ fontSize: '0.65rem', py: 0, minWidth: 0 }}
                      onClick={() => flagSession(s.id, !s.is_operator_preview)}
                    >
                      {s.is_operator_preview ? 'Unmark test' : 'Mark as test'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function DownloadGateDialog({ job, onClose, onSave }) {
  const [allow, setAllow] = useState(false);
  const [from, setFrom] = useState('');
  const [until, setUntil] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (!job) return;
    setAllow(!!job.allow_download);
    setFrom(isoToLocalInput(job.download_enabled_from));
    setUntil(isoToLocalInput(job.download_enabled_until));
    setError('');
    // Reset the saving flag — the parent closes us by setting job=null
    // (which makes us return null but does NOT unmount the component),
    // so on the next open the previous render's saving=true would stick
    // and the Save button would stay permanently disabled until the user
    // refreshed the whole page.
    setSaving(false);
  }, [job]);

  if (!job) return null;

  const handleSave = async () => {
    setError('');
    const fromIso = from ? new Date(from).toISOString() : null;
    const untilIso = until ? new Date(until).toISOString() : null;
    if (fromIso && untilIso && new Date(fromIso) >= new Date(untilIso)) {
      setError('Start must be earlier than end.');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        allow_download: allow,
        download_enabled_from: fromIso,
        download_enabled_until: untilIso,
      });
    } catch (e) {
      setError(e?.message || 'Failed to save');
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Download access</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {job.recipient_name} — {job.source_filename}
        </Typography>
        <FormControlLabel
          control={<Switch checked={allow} onChange={(e) => setAllow(e.target.checked)} />}
          label={allow ? 'Download enabled' : 'Watch-only (download hidden)'}
          sx={{ mb: 1 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Streaming is always available. Toggle controls only the Download button on the share link.
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, opacity: allow ? 1 : 0.5, pointerEvents: allow ? 'auto' : 'none' }}>
          <TextField
            label="Download opens (optional)"
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="Download closes (optional)"
            type="datetime-local"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <Typography variant="caption" color="text.secondary">
            Leave both blank for an always-open window.
          </Typography>
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Frame-capture audit panel — read-only operator view of every still
// the recipient grabbed. Renders only if allow_frame_capture is on or
// there's history to show. Failure rows surface in red so QC issues
// (slow CIFS reads, missing source) jump out.
function FrameCaptureAuditPanel({ jobId, jobAllowFrameCapture }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/api/jobs/${jobId}/frame-captures`);
        if (!cancelled) setRows(data || []);
      } catch (_) {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId]);

  if (!jobAllowFrameCapture && rows.length === 0) return null;
  return (
    <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'rgba(212,160,23,0.04)', borderLeft: '3px solid #d4a017', borderRadius: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#d4a017', display: 'block', mb: 1 }}>
        Captured Frames {rows.length > 0 ? `(${rows.length})` : ''}
      </Typography>
      {loading && rows.length === 0 ? (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Loading…</Typography>
      ) : rows.length === 0 ? (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          No captures yet — viewer can grab clean stills from the player.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ py: 0.25 }}>Recipient</TableCell>
              <TableCell sx={{ py: 0.25 }}>Timecode</TableCell>
              <TableCell sx={{ py: 0.25 }}>Status</TableCell>
              <TableCell sx={{ py: 0.25 }}>Captured</TableCell>
              <TableCell sx={{ py: 0.25 }}>IP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} sx={{ '& td': { py: 0.25, fontSize: '0.72rem' } }}>
                <TableCell>{r.recipient_name || r.recipient_email || `share #${r.share_id}`}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{r.timecode || '—'}</TableCell>
                <TableCell sx={{
                  color: r.status === 'failed' ? 'error.main' : (r.deleted_at ? 'text.disabled' : 'inherit')
                }}>
                  {r.deleted_at ? 'deleted' : r.status}{r.error_message ? ` · ${r.error_message.slice(0, 60)}` : ''}
                </TableCell>
                <TableCell>{fmtDateTime(r.created_at)}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{r.requested_by_ip || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

// Operator-side comments panel for a screener row. Loads on mount,
// gives mark-seen / mark-resolved / delete actions per comment. Hidden
// when allow_reviews is off and there are no historical comments to
// show — operators don't see noise on screeners that never had reviews.
// v10.21.11: comment status enum mirror of models.watch_comment
// COMMENT_STATUSES. Order here is the order shown in the dropdown.
const COMMENT_STATUS_OPTIONS = [
  { value: 'open',         label: 'Open',         color: 'warning' },
  { value: 'in_progress',  label: 'In progress',  color: 'info' },
  { value: 'resolved',     label: 'Resolved',     color: 'success' },
  { value: 'wont_fix',     label: "Won't fix",    color: 'default' },
];

function ReviewCommentsPanel({ jobId, jobAllowReviews, onChange }) {
  const [comments, setComments] = React.useState(null);  // null = loading
  const [busy, setBusy] = React.useState(false);
  // v10.21.10: per-comment edit state. id -> draft text. Presence
  // toggles the inline TextField; absence renders the read-only body.
  const [editingDraft, setEditingDraft] = React.useState({});
  // v10.21.11: per-comment reply composer state. id -> draft text.
  const [replyDraft, setReplyDraft] = React.useState({});
  // Reply edit state — keyed by reply id, holds the draft text.
  const [replyEditDraft, setReplyEditDraft] = React.useState({});

  // CRITICAL: don't put `onChange` in this useCallback's deps. The parent
  // passes a new arrow on every render, which would recreate `reload` on
  // every render, refire the mount effect, refire onChange (= dispatch
  // fetchJobs), force the parent to re-render, recreate onChange, and so
  // on. Render loop. Hit /api/jobs/ at React render cadence and tripped
  // the rate limit + emptied the Library.
  //
  // onChange is captured via closure when `reload` runs — and we now
  // only fire onChange from explicit user-action handlers (mark seen,
  // resolved, delete), never from reload itself. Initial mount doesn't
  // need to bubble: the parent already fetched its jobs list before
  // this panel mounted.
  const reload = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/api/jobs/${jobId}/comments`);
      setComments(data);
    } catch (_) {
      setComments([]);
    }
  }, [jobId]);

  React.useEffect(() => { reload(); }, [reload]);

  if (!jobAllowReviews && (comments?.length ?? 0) === 0) return null;
  if (comments === null) {
    return <Box sx={{ py: 1 }}><Typography variant="caption" color="text.secondary">Loading reviewer comments…</Typography></Box>;
  }

  const fmtTs = (s) => {
    const m = Math.floor(s / 60), sec = Math.floor(s % 60);
    const h = Math.floor(m / 60);
    const mm = (m % 60).toString().padStart(2, '0');
    const ss = sec.toString().padStart(2, '0');
    return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
  };
  const unseen = comments.filter(c => !c.seen_at).length;

  const bubble = () => { if (onChange) onChange(); };
  const markSeen = async (c, seen) => {
    setBusy(true);
    try { await api.patch(`/api/jobs/${jobId}/comments/${c.id}`, { seen }); await reload(); bubble(); }
    finally { setBusy(false); }
  };
  const markResolved = async (c, resolved) => {
    setBusy(true);
    try { await api.patch(`/api/jobs/${jobId}/comments/${c.id}`, { resolved }); await reload(); bubble(); }
    finally { setBusy(false); }
  };
  const remove = async (c) => {
    if (!window.confirm('Delete this comment? Soft-delete — kept in history but hidden from active views.')) return;
    setBusy(true);
    try { await api.delete(`/api/jobs/${jobId}/comments/${c.id}`); await reload(); bubble(); }
    finally { setBusy(false); }
  };
  const startEdit = (c) => setEditingDraft((d) => ({ ...d, [c.id]: c.body }));
  const cancelEdit = (c) => setEditingDraft((d) => { const n = { ...d }; delete n[c.id]; return n; });
  const saveEdit = async (c) => {
    const draft = (editingDraft[c.id] || '').trim();
    if (!draft) return;
    if (draft === c.body) { cancelEdit(c); return; }
    setBusy(true);
    try {
      await api.patch(`/api/jobs/${jobId}/comments/${c.id}`, { body: draft });
      cancelEdit(c);
      await reload();
      bubble();
    } finally { setBusy(false); }
  };

  // v10.21.11: status dropdown + reply CRUD handlers.
  const setStatus = async (c, status) => {
    if (status === c.status) return;
    setBusy(true);
    try {
      await api.patch(`/api/jobs/${jobId}/comments/${c.id}`, { status });
      await reload();
      bubble();
    } finally { setBusy(false); }
  };
  const openReply = (c) => setReplyDraft((d) => ({ ...d, [c.id]: '' }));
  const cancelReply = (c) => setReplyDraft((d) => { const n = { ...d }; delete n[c.id]; return n; });
  const submitReply = async (c) => {
    const draft = (replyDraft[c.id] || '').trim();
    if (!draft) return;
    setBusy(true);
    try {
      await api.post(`/api/jobs/${jobId}/comments/${c.id}/replies`, { body: draft });
      cancelReply(c);
      await reload();
      bubble();
    } finally { setBusy(false); }
  };
  const startReplyEdit = (r) => setReplyEditDraft((d) => ({ ...d, [r.id]: r.body }));
  const cancelReplyEdit = (r) => setReplyEditDraft((d) => { const n = { ...d }; delete n[r.id]; return n; });
  const saveReplyEdit = async (c, r) => {
    const draft = (replyEditDraft[r.id] || '').trim();
    if (!draft) return;
    if (draft === r.body) { cancelReplyEdit(r); return; }
    setBusy(true);
    try {
      await api.patch(`/api/jobs/${jobId}/comments/${c.id}/replies/${r.id}`, { body: draft });
      cancelReplyEdit(r);
      await reload();
      bubble();
    } finally { setBusy(false); }
  };
  const removeReply = async (c, r) => {
    if (!window.confirm('Delete this reply? Soft-delete — kept in history but hidden from the recipient.')) return;
    setBusy(true);
    try {
      await api.delete(`/api/jobs/${jobId}/comments/${c.id}/replies/${r.id}`);
      await reload();
      bubble();
    } finally { setBusy(false); }
  };
  const markAllSeen = async () => {
    if (unseen === 0) return;
    setBusy(true);
    try {
      for (const c of comments) {
        if (!c.seen_at) await api.patch(`/api/jobs/${jobId}/comments/${c.id}`, { seen: true });
      }
      await reload();
      bubble();
    } finally { setBusy(false); }
  };

  return (
    <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(212,160,23,0.04)', borderRadius: 1, border: '1px solid rgba(212,160,23,0.15)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="overline" color="text.secondary">Reviewer comments</Typography>
        <Chip size="small" label={comments.length} sx={{ height: 18, fontSize: '0.65rem' }} />
        {unseen > 0 && <Chip size="small" label={`${unseen} new`} color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />}
        <Box sx={{ flexGrow: 1 }} />
        {unseen > 0 && (
          <Button size="small" onClick={markAllSeen} disabled={busy}>Mark all seen</Button>
        )}
      </Box>
      {comments.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {jobAllowReviews
            ? 'No comments from the recipient yet. They can leave timestamped notes to the filmmakers from the watch page.'
            : 'No comments. Filmmaker comments are currently disabled for this screener.'}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {comments.map(c => {
            const seen = !!c.seen_at;
            const resolved = !!c.resolved_at;
            return (
              <Box key={c.id} sx={{
                display: 'flex', gap: 1, alignItems: 'flex-start',
                p: 1, borderRadius: 0.5,
                bgcolor: !seen ? 'rgba(212,160,23,0.08)' : 'rgba(0,0,0,0.15)',
                borderLeft: `3px solid ${resolved ? '#43a047' : (seen ? 'rgba(232,230,227,0.2)' : '#d4a017')}`,
                opacity: resolved ? 0.6 : 1,
              }}>
                <Box sx={{ minWidth: 60, fontFamily: 'monospace', fontSize: '0.72rem', color: '#d4a017' }}>
                  {fmtTs(c.timestamp_seconds)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {editingDraft[c.id] !== undefined ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <TextField
                        size="small"
                        multiline
                        minRows={2}
                        maxRows={8}
                        value={editingDraft[c.id]}
                        onChange={(e) => setEditingDraft((d) => ({ ...d, [c.id]: e.target.value.slice(0, 5000) }))}
                        autoFocus
                        sx={{ '& .MuiInputBase-input': { fontSize: '0.78rem' } }}
                      />
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Button size="small" variant="text" onClick={() => cancelEdit(c)} disabled={busy}>Cancel</Button>
                        <Button size="small" variant="contained" onClick={() => saveEdit(c)} disabled={busy || !editingDraft[c.id]?.trim()}>Save</Button>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="caption" sx={{ fontSize: '0.78rem', whiteSpace: 'pre-wrap', display: 'block' }}>
                      {c.body}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                      {c.viewer_name || c.viewer_email || 'Anonymous'} · {c.created_at ? fmtDateTime(c.created_at) : '—'}
                      {seen && ' · seen'}
                    </Typography>
                    {/* v10.21.11: status dropdown. Always visible
                        even on the inline edit row so the operator
                        can adjust state without leaving the box. */}
                    <FormControl size="small" sx={{ minWidth: 110 }}>
                      <Select
                        value={c.status || 'open'}
                        onChange={(e) => setStatus(c, e.target.value)}
                        disabled={busy}
                        sx={{
                          height: 22, fontSize: '0.7rem',
                          '& .MuiSelect-select': { py: 0, px: 0.75 },
                        }}
                      >
                        {COMMENT_STATUS_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.78rem' }}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* v10.21.11: reply thread + composer */}
                  {(c.replies || []).length > 0 && (
                    <Box sx={{ mt: 0.75, pl: 1.5, borderLeft: '2px solid rgba(212,160,23,0.25)', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {c.replies.map((r) => {
                        const isEditing = replyEditDraft[r.id] !== undefined;
                        return (
                          <Box key={r.id} sx={{ display: 'flex', flexDirection: 'column' }}>
                            {isEditing ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <TextField
                                  size="small" multiline minRows={2} maxRows={6}
                                  value={replyEditDraft[r.id]}
                                  onChange={(e) => setReplyEditDraft((d) => ({ ...d, [r.id]: e.target.value.slice(0, 5000) }))}
                                  autoFocus
                                  sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                                />
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                  <Button size="small" variant="text" onClick={() => cancelReplyEdit(r)} disabled={busy}>Cancel</Button>
                                  <Button size="small" variant="contained" onClick={() => saveReplyEdit(c, r)} disabled={busy || !replyEditDraft[r.id]?.trim()}>Save</Button>
                                </Box>
                              </Box>
                            ) : (
                              <>
                                <Typography variant="caption" sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                                  {r.body}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>
                                    {r.author_name || 'Operator'} · {r.created_at ? fmtDateTime(r.created_at) : ''}
                                    {r.updated_at && r.created_at && r.updated_at !== r.created_at ? ' · edited' : ''}
                                  </Typography>
                                  <Tooltip title="Edit reply">
                                    <IconButton size="small" onClick={() => startReplyEdit(r)} disabled={busy} sx={{ p: 0.25 }}>
                                      <span style={{ fontSize: 12 }}>✎</span>
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete reply">
                                    <IconButton size="small" onClick={() => removeReply(c, r)} disabled={busy} sx={{ p: 0.25 }}>
                                      <DeleteIcon sx={{ fontSize: 12 }} color="error" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  {replyDraft[c.id] !== undefined ? (
                    <Box sx={{ mt: 0.75, pl: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <TextField
                        size="small" multiline minRows={2} maxRows={6}
                        placeholder="Reply to the recipient..."
                        value={replyDraft[c.id]}
                        onChange={(e) => setReplyDraft((d) => ({ ...d, [c.id]: e.target.value.slice(0, 5000) }))}
                        autoFocus
                        sx={{ '& .MuiInputBase-input': { fontSize: '0.78rem' } }}
                      />
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Button size="small" variant="text" onClick={() => cancelReply(c)} disabled={busy}>Cancel</Button>
                        <Button size="small" variant="contained" onClick={() => submitReply(c)} disabled={busy || !replyDraft[c.id]?.trim()}>Send reply</Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 0.5 }}>
                      <Button
                        size="small" variant="text" onClick={() => openReply(c)} disabled={busy}
                        sx={{ fontSize: '0.7rem', py: 0, minWidth: 0 }}
                      >
                        ↪ Reply
                      </Button>
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.25 }}>
                  {editingDraft[c.id] === undefined && (
                    <>
                      {!seen && (
                        <Tooltip title="Mark as seen">
                          <IconButton size="small" onClick={() => markSeen(c, true)} disabled={busy}>
                            <span style={{ fontSize: 14 }}>✓</span>
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit (e.g. redact PII)">
                        <IconButton size="small" onClick={() => startEdit(c)} disabled={busy}>
                          <span style={{ fontSize: 14 }}>✎</span>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => remove(c)} disabled={busy}>
                          <DeleteIcon sx={{ fontSize: 14 }} color="error" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}


export default function WatermarkEncodeLibrary() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { jobs } = useSelector((state) => state.jobs);
  const { packages } = useSelector((state) => state.delivery);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const isSuperadmin = !!userInfo?.is_superadmin;
  const [deliverJob, setDeliverJob] = useState(null);

  // v10.19.56: snackbar feedback after row-level actions (resend,
  // bulk resend). Pre-fix the resend silently succeeded with no UI
  // signal — operator (bug_report #66) clicked the mail icon and
  // assumed nothing happened because the "Delivered" lifecycle chip
  // didn't change (that's by design — Delivered tracks download
  // completion, not email send time). Snackbar makes the action
  // observable.
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const toggleRow = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  const [statusFilter, setStatusFilter] = useState('');
  const [commentsFilter, setCommentsFilter] = useState('');  // '' | 'has' | 'unseen'
  // v10.20.12: archive view filter. 'active' hides archived rows
  // (the producer's default); 'archived' shows ONLY archived for the
  // organize-by-history view; 'all' shows both buckets together.
  const [archiveView, setArchiveView] = useState('active');
  // Per-row notes editor — keyed by job id, holds the draft text the
  // operator is typing. Cleared on save.
  const [notesDraft, setNotesDraft] = useState({});
  const [analyticsJob, setAnalyticsJob] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [downloadGateJob, setDownloadGateJob] = useState(null);

  // Bulk-ops state. Mirrors the Sources page pattern (utils/library.js
  // re-implements `shouldShowBulkOps` against a different localStorage
  // key so toggling Sources doesn't accidentally enable Library).
  const [bulkForce, setBulkForce] = useState(isLibraryBulkOpsForced());
  const [bulkAction, setBulkAction] = useState(null);
  const toggleBulkForce = () => {
    const next = !bulkForce;
    setBulkForce(next);
    setLibraryBulkOpsForced(next);
  };
  // Shared runner — handles selection, concurrency, progress.
  const bulk = useBulkRunner({ onComplete: () => handleRefresh() });
  const selectedJobIds = bulk.selectedIds;

  const openPreview = async (job) => {
    // iOS Safari blocks window.open() called AFTER an await: the
    // user-gesture context expires while the preview-link round-trip
    // resolves and the popup is silently dropped. Workaround: open a
    // blank tab synchronously inside the click handler, then point it
    // at the real URL once the API responds. Desktop browsers handle
    // this identically.
    const w = window.open('about:blank', '_blank');
    try {
      const r = await api.post(`/api/jobs/${job.id}/preview-link`);
      // Prefer the watch link when streaming is ready, otherwise the
      // landing page (which itself shows whichever buttons are enabled).
      const url = job.hls_status === 'ready' ? r.data.watch_url : r.data.url;
      if (w && !w.closed) {
        try { w.opener = null; } catch (_) { /* cross-origin opener detach */ }
        w.location.href = url;
      } else {
        // Popup was blocked entirely (e.g. popups disabled in Settings).
        // Fall back to navigating the current tab; the operator will use
        // back-button to return.
        window.location.href = url;
      }
    } catch (e) {
      if (w && !w.closed) w.close();

      alert(e.response?.data?.detail || `Preview link error: ${e.message}`);
    }
  };

  const openAnalytics = async (job, opts = {}) => {
    setAnalyticsJob(job);
    if (!opts.includeTest) {
      // Fresh open — clear stale data so the loader shows.
      setAnalyticsData(null);
    }
    setAnalyticsLoading(true);
    try {
      const url = `/api/jobs/${job.id}/watch-analytics${opts.includeTest ? '?include_test=true' : ''}`;
      const r = await api.get(url);
      setAnalyticsData(r.data);
    } catch (e) {
      setAnalyticsData({ error: e.response?.data?.detail || e.message });
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Show-archived toggle drives both the URL param and the displayed
  // rows. Default off — archive is opt-in viewing.
  const [showArchived, setShowArchived] = useState(false);

  // Server-side search across recipient name, email, source filename,
  // and unique_id. Debounced 300ms so we don't hammer /api/jobs on every
  // keystroke. The text input value is the raw box state; the debounced
  // value is what gets sent to the backend.
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    dispatch(fetchJobs({ limit: 500, includeArchived: showArchived, view: archiveView, search: searchDebounced }));
    dispatch(fetchPackages({ sourceType: 'screener' }));
  }, [dispatch, showArchived, archiveView, searchDebounced]);

  // While any package is mid-sync ("Preparing"), poll the packages list every
  // 5s so the UI flips to "Delivered" automatically without a manual refresh.
  // Also fires while a download-enabled job has NO package row yet
  // (canonical-MP4 derivation phase) AND while a synced package is still
  // running its hash phase — the ring needs both to keep advancing.
  const hasPrepInFlight = (() => {
    const unsynced = (packages || []).some(
      (p) => p.source_type === 'screener' && !p.synced && !p.revoked,
    );
    if (unsynced) return true;
    // Hash still running on any synced screener package?
    if ((packages || []).some(
      (p) => p.source_type === 'screener' && p.synced && !p.revoked
        && p.hash_status && p.hash_status !== 'done',
    )) return true;
    // A downloadable Job whose Package row hasn't been created yet is
    // also "in flight" for the ring — the canonical-MP4 derivation
    // hasn't produced the file yet. Without this, the ring sits at
    // phase=preparing forever waiting for a Package that never comes
    // because we stopped polling.
    const allowingButNoPkg = (jobs || []).some((j) => {
      if (!j.allow_download || j.status !== 'completed') return false;
      const p = (packages || []).find(
        (x) => x.source_job_id === j.id && x.source_type === 'screener',
      );
      return !p;
    });
    return allowingButNoPkg;
  })();
  useEffect(() => {
    if (!hasPrepInFlight) return undefined;
    const t = setInterval(() => {
      dispatch(fetchPackages({ sourceType: 'screener' }));
      // Refresh jobs too — the canonical-MP4 derivation flips the
      // hls_status / lifecycle_stage fields used by the ring's Phase 1
      // detection.
      dispatch(fetchJobs({ limit: 500, includeArchived: showArchived, view: archiveView, search: searchDebounced }));
    }, 5000);
    return () => clearInterval(t);
    // `jobs` is intentionally NOT in deps: the closure above doesn't
    // read it. Including it caused the interval to clear+recreate on
    // every fetchJobs completion, drifting the 5s cadence and churning
    // the timer object. We only need the flag + the fetch parameters.
  }, [hasPrepInFlight, dispatch, showArchived, archiveView, searchDebounced]);

  // Library shows finished work only — completed (and failed, for visibility
  // into recent failures). Cancelled jobs live in the queue so the user can
  // restart them; we don't duplicate them here.
  //
  // A job whose Phase-1 MP4 is COMPLETED but whose HLS rendition is still
  // queued/starting/encoding/failed is NOT shippable yet — it shows in the
  // Queue with a "Stream encoding X%" chip. It should NOT also appear here
  // (the user saw the same row in both places, which made it look like the
  // job was finishing twice). Surface in Library only once HLS is in a
  // terminal state (ready/cancelled/null-when-skipped).
  const TERMINAL = new Set(['completed', 'failed']);
  const HLS_ACTIVE = new Set(['queued', 'starting', 'encoding', 'failed']);
  const allTerminal = (jobs || []).filter((j) => {
    if (!TERMINAL.has(j.status)) return false;
    // Failed Phase-1 jobs always show in Library so the operator sees them.
    if (j.status === 'failed') return true;
    // Phase-1 completed: only surface here once HLS is done (or skipped).
    return !HLS_ACTIVE.has(j.hls_status);
  });
  // Status filter — `stalled` is a synthetic filter that doesn't map
  // to a `j.status` value; route it through the dedicated helper.
  const filteredByStatus = !statusFilter
    ? allTerminal
    : statusFilter === 'stalled'
      ? allTerminal.filter((j) => isStalled(j))
      : allTerminal.filter((j) => j.status === statusFilter);

  // Comments filter (v10.19.57). 'has' = at least one filmmaker
  // comment ever recorded; 'unseen' = at least one not yet
  // acknowledged by the operator. Backend attaches both counts via
  // a single grouped SELECT in api/jobs.py.
  const filtered = !commentsFilter
    ? filteredByStatus
    : commentsFilter === 'has'
      ? filteredByStatus.filter((j) => (j.total_comment_count || 0) > 0)
      : filteredByStatus.filter((j) => (j.unseen_comment_count || 0) > 0);

  const getPackage = (jobId) =>
    (packages || []).find((p) => p.source_job_id === jobId && p.source_type === 'screener');

  const { rows, sortProps } = useSortableTable(
    filtered,
    'completed_at',
    'desc',
    {
      source_filename: (r) => (r.source_filename || '').toLowerCase(),
      recipient_name: (r) => (r.recipient_name || '').toLowerCase(),
      // Submitter — sorts by who clicked "Create encode" so an
      // operator scanning a shared workspace can group their own work.
      created_by_name: (r) => (r.created_by_name || '').toLowerCase(),
      output_size_bytes: (r) => Number(r.output_size_bytes || 0),
      completed_at: (r) => (r.completed_at ? new Date(r.completed_at).getTime() : 0),
      created_at: (r) => (r.created_at ? new Date(r.created_at).getTime() : 0),
      // Sort by the backend-computed `delivered_at` (oldest non-revoked
      // share.email_sent_at). Falls back to package.created_at for jobs
      // whose lifecycle attach failed (defensive — those rows still
      // need to be sortable).
      delivered_at: (r) => {
        if (r.delivered_at) return new Date(r.delivered_at).getTime();
        const p = getPackage(r.id);
        return p?.created_at ? new Date(p.created_at).getTime() : 0;
      },
      last_activity_at: (r) => (r.last_activity_at ? new Date(r.last_activity_at).getTime() : 0),
      // Status sort uses lifecycle_stage rank when present (newer field
      // surfaces "Watching" / "Downloaded" / etc.) and falls back to
      // job.status for jobs that haven't had lifecycle attached yet.
      lifecycle_stage: (r) => (r.lifecycle_stage || r.status || '').toLowerCase(),
    },
  );

  // v10.21.1: deep-link handler. The "Just give me the link" email's
  // primary button hands the operator a URL like /library?share=123
  // (and we'll add /library?job=456 for callers that don't know the
  // share id). Find the row, expand it, scroll to it, and clear the
  // query param so a refresh doesn't keep re-firing the scroll.
  // Filed against bug #67 (Marty Hernandez): pre-v10.21.1 the email's
  // button pointed at /d/{token}, which routed the operator through
  // the recipient NDA gate as if they were the recipient. Deep-link
  // sidesteps that entirely.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkScrollDone = useRef(false);
  useEffect(() => {
    if (deepLinkScrollDone.current) return;
    const shareIdParam = searchParams.get('share');
    const jobIdParam = searchParams.get('job');
    if (!shareIdParam && !jobIdParam) return;
    // Need packages loaded to resolve share->job, and rows computed
    // for the target job to be expandable. Bail until both arrive.
    if (!packages || packages.length === 0) return;
    if (!rows || rows.length === 0) return;
    let targetJobId = jobIdParam ? Number(jobIdParam) : null;
    if (!targetJobId && shareIdParam) {
      const sid = Number(shareIdParam);
      const owner = (packages || []).find((p) =>
        (p.shares || []).some((s) => Number(s.id) === sid),
      );
      if (owner && owner.source_job_id) targetJobId = owner.source_job_id;
    }
    if (!targetJobId) return;
    setExpandedRows((prev) => ({ ...prev, [targetJobId]: true }));
    // Defer the scroll one tick so the Collapse has mounted.
    setTimeout(() => {
      const el = document.getElementById(`library-row-${targetJobId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    // Strip the param so subsequent navigations don't keep firing.
    const next = new URLSearchParams(searchParams);
    next.delete('share');
    next.delete('job');
    setSearchParams(next, { replace: true });
    deepLinkScrollDone.current = true;
  }, [searchParams, packages, rows, setSearchParams]);

  const handleRefresh = () => {
    dispatch(fetchJobs({ limit: 500 }));
    dispatch(fetchPackages({ sourceType: 'screener' }));
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    await dispatch(deleteJob(deleteConfirm.id));
    setDeleteConfirm(null);
    handleRefresh();
  };

  const handleRestart = (job) => {
    dispatch(restartJob(job.id)).then(handleRefresh);
  };

  // Aggregate stats — operator's at-a-glance health of the day.
  // Computed across the FULL job set (not the filtered view) so the
  // counters stay accurate when the operator is filtered down.
  const aggregates = computeLibraryAggregates(jobs || []);

  // Bulk-ops visibility + select-all-visible helpers. `bulkVisible`
  // gates the checkbox column + the action bar; select-all targets
  // the currently-rendered (filtered + sorted) row set, not the full
  // workspace, because the operator's intent with "select all" is
  // "everything I can see right now."
  const bulkVisible = shouldShowLibraryBulkOps(
    (jobs || []).length, isSuperadmin, bulkForce,
  );
  const visibleRowIds = rows.map((j) => j.id);

  // Wrapped wrappers so each bulk button can `await` and then close
  // the action dialog when done — the runner handles selection+
  // progress, the page closes its own confirm.
  const runBulk = async (action) => {
    await bulk.run(action);
    setBulkAction(null);
  };

  const doBulkArchive = () => runBulk((id) => dispatch(deleteJob(id)).unwrap());
  const doBulkRevoke = async () => {
    await runBulk(async (id) => {
      const pkg = (packages || []).find(
        (p) => p.source_job_id === id && p.source_type === 'screener' && !p.revoked,
      );
      if (!pkg) return; // nothing to revoke
      await api.post(`/api/delivery/packages/${pkg.id}/revoke`);
    });
  };
  const doBulkResend = async () => {
    let sentCount = 0;
    let errorCount = 0;
    await runBulk(async (id) => {
      const pkg = (packages || []).find(
        (p) => p.source_job_id === id && p.source_type === 'screener' && !p.revoked,
      );
      if (!pkg) return;
      const targetShares = (pkg.shares || []).filter((s) => !s.revoked_at);
      for (const share of targetShares) {
        try {
          await api.post(`/api/delivery/shares/${share.id}/resend`);
          sentCount++;
        } catch (_) {
          errorCount++;
        }
      }
    });
    setSnackbar({
      open: true,
      severity: errorCount > 0 ? 'warning' : 'success',
      message: errorCount > 0
        ? `Resent ${sentCount} email${sentCount === 1 ? '' : 's'}, ${errorCount} failed.`
        : `Resent ${sentCount} delivery email${sentCount === 1 ? '' : 's'}.`,
    });
  };

  return (
    <Box>
      {(jobs && jobs.length > 0) && (
        <Box
          sx={{
            display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center',
            mb: 2, p: 1.5, borderRadius: 1,
            border: '1px solid', borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">Today</Typography>
            <Typography variant="h6">
              {aggregates.completedToday}
              {aggregates.deliveredToday > 0 && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  · {aggregates.deliveredToday} delivered
                </Typography>
              )}
            </Typography>
          </Box>
          {aggregates.inFlight > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">In flight</Typography>
              <Typography variant="h6" color="warning.main">{aggregates.inFlight}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary">Delivered</Typography>
            <Typography variant="h6" color="success.main">{aggregates.delivered}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Watched</Typography>
            <Typography variant="h6" color="success.main">{aggregates.watched}</Typography>
          </Box>
          {aggregates.stalled > 0 && (
            <Tooltip title="Delivered but no recipient signal in 72+ hours. Click to filter.">
              <Box
                sx={{ cursor: 'pointer' }}
                onClick={() => setStatusFilter('stalled')}
              >
                <Typography variant="caption" color="text.secondary">Stalled</Typography>
                <Typography variant="h6" color="warning.main">{aggregates.stalled}</Typography>
              </Box>
            </Tooltip>
          )}
          {aggregates.failed > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">Failed</Typography>
              <Typography variant="h6" color="error.main">{aggregates.failed}</Typography>
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {filtered.length} encode{filtered.length === 1 ? '' : 's'}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="stalled">Delivered, not opened (72h+)</MenuItem>
            </Select>
          </FormControl>
          {/* v10.19.57: filter by recipient comments addressed to the
              filmmaker. "Has comments" = at least one watch_comment ever
              (seen or unseen). "Unseen only" = at least one comment the
              operator hasn't acknowledged. Lets a producer skim the
              page for "where's the feedback?". */}
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Comments</InputLabel>
            <Select value={commentsFilter} label="Comments" onChange={(e) => setCommentsFilter(e.target.value)}>
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="has">Has filmmaker comments</MenuItem>
              <MenuItem value="unseen">Unseen only</MenuItem>
            </Select>
          </FormControl>
          {/* v10.20.12: archive view. Default 'Active' keeps the
              producer's distribution list focused; flip to 'Archived'
              to see past releases without losing any stats. */}
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Show</InputLabel>
            <Select value={archiveView} label="Show" onChange={(e) => setArchiveView(e.target.value)}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="Search recipient, source, ID, or email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 280 }}
            InputProps={{
              endAdornment: searchInput ? (
                <IconButton size="small" onClick={() => setSearchInput('')} aria-label="Clear search">
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : null,
            }}
          />
        </Box>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
          }
          label="Show archived"
          sx={{ ml: 1, '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: 'text.secondary' } }}
        />
        {isSuperadmin && (jobs?.length || 0) < 10 && (
          <Tooltip title="Operator-only: force show bulk-ops UI on small fixture workspaces">
            <FormControlLabel
              control={
                <Switch size="small" checked={bulkForce} onChange={toggleBulkForce} />
              }
              label="Bulk ops"
              sx={{ ml: 1, '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: 'text.secondary' } }}
            />
          </Tooltip>
        )}
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      <ActiveTransfersPanel sourceType="screener" />

      {bulkVisible && (
        <BulkActionBar
          selectedCount={selectedJobIds.size}
          busy={bulk.busy}
          progress={bulk.progress}
          onClear={bulk.clearSelection}
          actions={
            <>
              <Button size="small" disabled={bulk.busy}
                onClick={() => setBulkAction({ kind: 'resend' })}>
                Resend delivery emails
              </Button>
              <Button size="small" color="warning" disabled={bulk.busy}
                onClick={() => setBulkAction({ kind: 'revoke' })}>
                Revoke shares
              </Button>
              <Button size="small" color="error" variant="contained" disabled={bulk.busy}
                onClick={() => setBulkAction({ kind: 'archive' })}>
                Archive
              </Button>
            </>
          }
        />
      )}

      {rows.length === 0 ? (
        <Alert severity="info">No watermark encodes here yet.</Alert>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                {bulkVisible && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      indeterminate={bulk.someVisibleSelected(visibleRowIds) && !bulk.allVisibleSelected(visibleRowIds)}
                      checked={bulk.allVisibleSelected(visibleRowIds)}
                      onChange={() => bulk.toggleSelectAllVisible(visibleRowIds)}
                      disabled={rows.length === 0}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ width: 32, p: 0 }} />
                {/* Recipient leads — that's the operator's primary scan
                    target ("who's this delivery for"). Source moved to
                    the second column. */}
                <TableCell><TableSortLabel {...sortProps('recipient_name')}>Recipient</TableSortLabel></TableCell>
                <TableCell><TableSortLabel {...sortProps('source_filename')}>Source</TableSortLabel></TableCell>
                <TableCell sx={{ width: 140 }}><TableSortLabel {...sortProps('created_by_name')}>Submitted by</TableSortLabel></TableCell>
                <TableCell sx={{ width: 180 }}><TableSortLabel {...sortProps('delivered_at')}>Delivered</TableSortLabel></TableCell>
                <TableCell sx={{ width: 130 }}><TableSortLabel {...sortProps('last_activity_at')}>Last activity</TableSortLabel></TableCell>
                <TableCell sx={{ width: 160 }}><TableSortLabel {...sortProps('lifecycle_stage')}>Status</TableSortLabel></TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((job) => {
                const pkg = getPackage(job.id);
                const isCompleted = job.status === 'completed';
                const expanded = !!expandedRows[job.id];
                return (
                  <React.Fragment key={job.id}>
                  <TableRow id={`library-row-${job.id}`} hover selected={selectedJobIds.has(job.id)} sx={{ '& > *': { borderBottom: expanded ? 'unset' : undefined } }}>
                    {bulkVisible && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedJobIds.has(job.id)}
                          onChange={() => bulk.toggleSelected(job.id)}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ width: 32, p: 0.25 }}>
                      {(pkg && (pkg.shares || []).length > 0) ? (
                        <Tooltip title={expanded ? 'Hide details' : 'Show deliveries'}>
                          <IconButton size="small" onClick={() => toggleRow(job.id)}>
                            {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      ) : isCompleted ? (
                        <Tooltip title="No deliveries yet — deliver first to expand details">
                          <span>
                            <IconButton size="small" disabled>
                              <ExpandIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} noWrap>{job.recipient_name}</Typography>
                      {/* v10.20.12: notes snippet under the recipient name.
                          Producer's pinned context (Sundance press push,
                          sales-agent ask, etc.) visible without expanding
                          the row. Italics + secondary color keep it
                          structurally subordinate to the name. */}
                      {job.notes && (
                        <Tooltip title={job.notes} arrow placement="bottom-start">
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block', color: 'text.secondary', fontStyle: 'italic',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              maxWidth: 240, cursor: 'help',
                            }}
                          >
                            “{job.notes}”
                          </Typography>
                        </Tooltip>
                      )}
                      {job.archived_at && (
                        <Chip
                          size="small"
                          label="archived"
                          sx={{ height: 16, fontSize: '0.6rem', mt: 0.25, bgcolor: 'action.selected', color: 'text.secondary' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" noWrap>{job.source_filename}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {job.created_by_name || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {/* Delivered column. Falls back to "Submitted Xh ago"
                          when the screener hasn't gone out yet — operator
                          still sees a meaningful timestamp instead of an
                          empty cell. Tooltip carries the absolute time. */}
                      {job.delivered_at ? (
                        <Tooltip title={fmtDateTime(job.delivered_at)}>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
                              {formatRelativeTime(job.delivered_at)}
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Tooltip title={job.created_at ? `Submitted ${fmtDateTime(job.created_at)}` : ''}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {job.created_at ? `Submitted ${formatRelativeTime(job.created_at)}` : '—'}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* Last-activity column. Empty when delivered but no
                          recipient signal yet — operator can scan for
                          "delivered, no activity" rows visually. */}
                      {job.last_activity_at ? (
                        <Tooltip title={fmtDateTime(job.last_activity_at)}>
                          <Typography variant="caption" sx={{ color: 'info.main' }}>
                            {formatRelativeTime(job.last_activity_at)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <LifecycleStatus job={job} />
                    </TableCell>
                    <TableCell align="right">
                      {isCompleted && (
                        <Tooltip title={pkg ? 'Re-deliver' : 'Deliver'}>
                          <IconButton
                            size="small"
                            onClick={() => setDeliverJob(job)}
                            sx={{ color: alpha(theme.palette.primary.main, 0.85) }}
                          >
                            <DeliverIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {job.status === 'failed' && (
                        <Tooltip title="Restart">
                          <IconButton size="small" onClick={() => handleRestart(job)}>
                            <RestartIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Expand/collapse caret moved to the leftmost
                          column so the action cluster on the right stays
                          focused on actual actions (deliver / preview /
                          download / reviews / analytics / archive). */}
                      {isCompleted && (pkg && (pkg.shares || []).length > 0 ? (
                        <Tooltip title="Preview as recipient (skips NDA)">
                          <IconButton size="small" onClick={() => openPreview(job)}>
                            <PreviewIcon fontSize="small" sx={{ color: alpha(theme.palette.primary.main, 0.85) }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Deliver first to enable recipient preview">
                          <span>
                            <IconButton size="small" disabled>
                              <PreviewIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ))}
                      {/* Download / Reviews / Frame-capture toggles
                          relocated to the expanded delivery panel — they
                          were three pixel-identical IconButtons mixed
                          into the one-shot action cluster, easy to bump
                          by accident and easy to mistake for actions
                          rather than persistent settings. The expanded
                          panel groups them under "Permissions for this
                          encode."

                          The download-prep state still surfaces here as
                          a quiet ring around the analytics icon when a
                          download is in flight — operators don't need
                          to expand a row to see "still preparing."  */}
                      {isCompleted && (() => {
                        const prep = getDownloadPrepState(job, pkg);
                        if (prep.phase === 'off' || prep.phase === 'revoked' || prep.phase === 'ready') {
                          return null;
                        }
                        const verifying = prep.phase === 'verifying';
                        return (
                          <Tooltip title={prep.label}>
                            <IconButton size="small" onClick={() => toggleRow(job.id)} sx={{ position: 'relative' }}>
                              <CircularProgress
                                variant="determinate"
                                value={prep.progress}
                                size={20}
                                thickness={4}
                                sx={{
                                  color: verifying ? 'success.main' : 'warning.main',
                                  position: 'absolute', inset: 0, margin: 'auto',
                                }}
                              />
                              <DownloadFilledIcon
                                sx={{ fontSize: 12, color: verifying ? '#43a047' : 'rgba(232,230,227,0.55)' }}
                              />
                            </IconButton>
                          </Tooltip>
                        );
                      })()}
                      {isCompleted && (
                        <Tooltip title="Watch analytics">
                          <IconButton size="small" onClick={() => openAnalytics(job)}>
                            <AnalyticsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* v10.20.12: archive is now a real action distinct
                          from delete. Archive moves the row out of the
                          default Library view but keeps every share, watch
                          session, and download event — follow-viewer keeps
                          working. Delete removes the files from NAS/MinIO
                          (metrics still preserved in the DB but the row
                          falls out of normal views). */}
                      {job.deleted_at ? (
                        <Tooltip title="Restore (was deleted)">
                          <IconButton size="small" sx={{ color: '#43a047' }}
                            onClick={async () => {
                              try {
                                await dispatch(restoreJob(job.id)).unwrap();
                                dispatch(fetchJobs({ limit: 500, includeArchived: showArchived, view: archiveView, search: searchDebounced }));
                              } catch (_) { /* swallow */ }
                            }}>
                            <UnarchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : job.archived_at ? (
                        <Tooltip title="Un-archive (return to active Library)">
                          <IconButton size="small" sx={{ color: '#d4a017' }}
                            onClick={async () => {
                              try {
                                await api.post(`/api/jobs/${job.id}/unarchive`);
                                setSnackbar({ open: true, severity: 'success', message: 'Returned to active.' });
                                dispatch(fetchJobs({ limit: 500, includeArchived: showArchived, view: archiveView, search: searchDebounced }));
                              } catch (e) {
                                setSnackbar({ open: true, severity: 'error', message: e?.response?.data?.detail || 'Un-archive failed.' });
                              }
                            }}>
                            <UnarchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Archive (hide from active Library; stats + follow-viewer preserved)">
                          <IconButton size="small"
                            onClick={async () => {
                              try {
                                await api.post(`/api/jobs/${job.id}/archive`);
                                setSnackbar({ open: true, severity: 'success', message: 'Archived. View again under Show → Archived.' });
                                dispatch(fetchJobs({ limit: 500, includeArchived: showArchived, view: archiveView, search: searchDebounced }));
                              } catch (e) {
                                setSnackbar({ open: true, severity: 'error', message: e?.response?.data?.detail || 'Archive failed.' });
                              }
                            }}>
                            <ArchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete (removes files; metrics preserved in audit log)">
                        <IconButton size="small" color="error" onClick={() => setDeleteConfirm(job)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  {expanded && pkg && (
                    <TableRow>
                      <TableCell colSpan={bulkVisible ? 9 : 8} sx={{ py: 0, pl: 4 }}>
                        <Collapse in={expanded}>
                          <Box sx={{ py: 1 }}>
                            {/* v10.20.12: inline notes editor. Lives at the
                                top of the expand panel so the producer can
                                pin context immediately without scrolling
                                past lifecycle + shares + comments. Saves on
                                blur; clears the local draft on success. */}
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
                              <TextField
                                size="small"
                                fullWidth
                                multiline
                                minRows={1}
                                maxRows={4}
                                placeholder="Notes for the team (Sundance press push, sales-agent ask, who saw this when — anything useful)"
                                value={notesDraft[job.id] !== undefined ? notesDraft[job.id] : (job.notes || '')}
                                onChange={(e) => setNotesDraft((d) => ({ ...d, [job.id]: e.target.value }))}
                                onBlur={async () => {
                                  if (notesDraft[job.id] === undefined) return;
                                  if (notesDraft[job.id] === (job.notes || '')) {
                                    setNotesDraft((d) => { const n = { ...d }; delete n[job.id]; return n; });
                                    return;
                                  }
                                  try {
                                    await api.patch(`/api/jobs/${job.id}/notes`, { notes: notesDraft[job.id] || null });
                                    setNotesDraft((d) => { const n = { ...d }; delete n[job.id]; return n; });
                                    dispatch(fetchJobs({ limit: 500, includeArchived: showArchived, view: archiveView, search: searchDebounced }));
                                  } catch (e) {
                                    setSnackbar({ open: true, severity: 'error', message: e?.response?.data?.detail || 'Could not save note.' });
                                  }
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
                              />
                            </Box>
                            {/* Compact header: encode-level facts that aren't
                                already on the row (Delivered, Size when known).
                                Per-share watch timing is in the table below
                                where it can vary per recipient — pulling it
                                up to the header would aggregate away that
                                detail in the multi-share case. */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1, fontSize: '0.7rem', color: 'text.secondary' }}>
                              {pkg && (
                                <Box>
                                  <strong>Delivered:</strong> {pkg.revoked ? 'Revoked' : (pkg.synced ? `Yes · ${fmtDateTime(pkg.created_at)}` : 'Preparing…')}
                                </Box>
                              )}
                              {Number(job.output_size_bytes) > 0 && (
                                <Box><strong>Size:</strong> {fmtSize(job.output_size_bytes)}</Box>
                              )}
                            </Box>

                            {/* Per-encode permission toggles. Moved out of
                                the action-icon cluster on the row (where
                                they were one-click flips between toggles
                                and one-shot actions and easy to bump by
                                accident) into the expanded panel where
                                they sit alongside the share-level details
                                they affect. Each toggle still hits the
                                same /api/jobs/:id/{reviews,frame-capture,
                                download} endpoints — just at a different
                                location. */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mb: 0.25 }}>
                                Permissions for this encode
                              </Typography>
                              <FormControlLabel
                                sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                                control={
                                  <Switch
                                    size="small"
                                    checked={!!job.allow_reviews}
                                    onChange={async () => {
                                      // v10.21.19: surface errors instead of swallowing,
                                      // and refetch with the SAME args as the page-level
                                      // fetch so the result matches the operator's
                                      // active filter (archiveView / search). Bare
                                      // fetchJobs() returned a different list shape
                                      // that occasionally made it look like the toggle
                                      // didn't stick.
                                      const next = !job.allow_reviews;
                                      try {
                                        await api.patch(`/api/jobs/${job.id}/reviews`, { allow_reviews: next });
                                        dispatch(fetchJobs({
                                          limit: 500,
                                          includeArchived: showArchived,
                                          view: archiveView,
                                          search: searchDebounced,
                                        }));
                                        setSnackbar({ open: true, severity: 'success',
                                          message: next ? 'Filmmaker comments enabled' : 'Filmmaker comments disabled' });
                                      } catch (e) {
                                        const detail = e?.response?.data?.detail || e?.message || 'unknown error';
                                        setSnackbar({ open: true, severity: 'error',
                                          message: `Could not toggle filmmaker comments: ${detail}` });
                                      }
                                    }}
                                  />
                                }
                                label={
                                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    {job.allow_reviews ? <CommentIcon fontSize="inherit" /> : <CommentOffIcon fontSize="inherit" />}
                                    Filmmaker comments
                                    {(job.unseen_comment_count || 0) > 0 && (
                                      <Badge
                                        badgeContent={job.unseen_comment_count}
                                        color="warning"
                                        sx={{ ml: 0.75, '& .MuiBadge-badge': { fontSize: 9, height: 14, minWidth: 14, position: 'static', transform: 'none' } }}
                                      />
                                    )}
                                  </Box>
                                }
                              />
                              <FormControlLabel
                                sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                                control={
                                  <Switch
                                    size="small"
                                    checked={!!job.allow_frame_capture}
                                    onChange={async () => {
                                      const next = !job.allow_frame_capture;
                                      try {
                                        await api.patch(`/api/jobs/${job.id}/frame-capture`, { allow_frame_capture: next });
                                        dispatch(fetchJobs({
                                          limit: 500,
                                          includeArchived: showArchived,
                                          view: archiveView,
                                          search: searchDebounced,
                                        }));
                                        setSnackbar({ open: true, severity: 'success',
                                          message: next ? 'Frame capture enabled' : 'Frame capture disabled' });
                                      } catch (e) {
                                        const detail = e?.response?.data?.detail || e?.message || 'unknown error';
                                        setSnackbar({ open: true, severity: 'error',
                                          message: `Could not toggle frame capture: ${detail}` });
                                      }
                                    }}
                                  />
                                }
                                label={
                                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    {job.allow_frame_capture ? <CameraIcon fontSize="inherit" /> : <CameraOffIcon fontSize="inherit" />}
                                    Frame capture
                                  </Box>
                                }
                              />
                              <Tooltip title={downloadStatusLabel(job)}>
                                <FormControlLabel
                                  sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                                  control={
                                    <Switch
                                      size="small"
                                      checked={!!job.allow_download}
                                      onChange={() => setDownloadGateJob(job)}
                                    />
                                  }
                                  label={
                                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                      {job.allow_download ? <DownloadOnIcon fontSize="inherit" /> : <DownloadOffIcon fontSize="inherit" />}
                                      Download
                                    </Box>
                                  }
                                />
                              </Tooltip>
                              {/* v10.21.26: per-screener NDA toggle on the
                                  distribution row. Operator request: studio
                                  legal sometimes returns "we don't sign
                                  these on consideration prints" — operator
                                  flips this off, resends, recipient lands
                                  straight on the player. Default in v10.21.17
                                  is ON; flipping off here is forward-only
                                  on the next page load (helper
                                  _share_needs_nda reads the column each
                                  request). */}
                              <Tooltip title={job.nda_required === false
                                ? 'Confidentiality gate OFF — recipient lands on the player directly. Use only when the recipient is contractually unwilling to sign on consideration prints.'
                                : 'Confidentiality gate ON — recipient must accept terms on the landing page before watching. Default behavior.'}>
                                <FormControlLabel
                                  sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                                  control={
                                    <Switch
                                      size="small"
                                      checked={job.nda_required !== false}
                                      onChange={async () => {
                                        const next = job.nda_required === false;
                                        try {
                                          await api.patch(`/api/jobs/${job.id}/nda-required`, { nda_required: next });
                                          dispatch(fetchJobs({
                                            limit: 500,
                                            includeArchived: showArchived,
                                            view: archiveView,
                                            search: searchDebounced,
                                          }));
                                          setSnackbar({ open: true, severity: 'success',
                                            message: next
                                              ? 'Confidentiality agreement required again'
                                              : 'Confidentiality agreement waived for this screener' });
                                        } catch (e) {
                                          const detail = e?.response?.data?.detail || e?.message || 'unknown error';
                                          setSnackbar({ open: true, severity: 'error',
                                            message: `Could not toggle NDA: ${detail}` });
                                        }
                                      }}
                                    />
                                  }
                                  label={
                                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                      {job.nda_required !== false ? <NdaOnIcon fontSize="inherit" /> : <NdaOffIcon fontSize="inherit" />}
                                      Confidentiality agreement
                                    </Box>
                                  }
                                />
                              </Tooltip>
                            </Box>

                            {/* v10.20.14: call out re-delivery groupings.
                                When the package has multiple shares
                                landing on the same recipient_email,
                                show a small chip strip at the top of
                                the share table so the operator sees
                                "Jay Mohr — 2 deliveries" without having
                                to scan the whole list and notice
                                duplicate emails. */}
                            {(() => {
                              const counts = new Map();
                              for (const s of (pkg.shares || [])) {
                                const k = (s.recipient_email || '').toLowerCase();
                                if (!k) continue;
                                counts.set(k, (counts.get(k) || 0) + 1);
                              }
                              const multi = [...counts.entries()].filter(([, n]) => n > 1);
                              if (multi.length === 0) return null;
                              return (
                                <Box sx={{
                                  display: 'flex', flexWrap: 'wrap', gap: 0.75,
                                  mb: 1, pb: 0.75, borderBottom: 1, borderColor: 'divider',
                                }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>
                                    Multi-delivery to:
                                  </Typography>
                                  {multi.map(([email, n]) => {
                                    const display = (pkg.shares || []).find(s => (s.recipient_email || '').toLowerCase() === email);
                                    const name = display?.recipient_name && display.recipient_name !== job.recipient_name ? display.recipient_name : (display?.recipient_email || email);
                                    return (
                                      <Chip
                                        key={email}
                                        size="small"
                                        label={`${name} · ${n} deliveries`}
                                        sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'action.selected' }}
                                      />
                                    );
                                  })}
                                </Box>
                              );
                            })()}
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  {/* Sub-table column order matches the per-
                                      share lifecycle: who's it for → when did
                                      they start → when did they finish → when
                                      does it expire → what's the current state →
                                      operator actions. Created (share-mint time)
                                      dropped — same temporal class as Watch
                                      started, and Delivered in the compact
                                      header already covers when the first share
                                      went out. */}
                                  <TableCell sx={{ py: 0.5 }}>Recipient</TableCell>
                                  <TableCell sx={{ py: 0.5 }}>Watch started</TableCell>
                                  <TableCell sx={{ py: 0.5 }}>Watch finished</TableCell>
                                  <TableCell sx={{ py: 0.5 }}>Expires</TableCell>
                                  <TableCell sx={{ py: 0.5 }}>Status</TableCell>
                                  <TableCell sx={{ py: 0.5 }} align="right" />
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(pkg.shares || []).map(s => (
                                  <TableRow key={s.id}>
                                    {/* Recipient cell. Email is always shown
                                        (parent row only carries the name, so
                                        the email is net-new). The name is
                                        added when it differs from the parent
                                        — re-delivery to a new contact case.
                                        Single-share with matching name reads
                                        as just the email, no duplication. */}
                                    <TableCell sx={{ py: 0.5 }}>
                                      <Stack spacing={0}>
                                        {(s.recipient_name && s.recipient_name !== job.recipient_name) && (
                                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                            {s.recipient_name}
                                          </Typography>
                                        )}
                                        <Typography variant="caption" color="text.secondary">
                                          {s.recipient_email || '—'}
                                        </Typography>
                                      </Stack>
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                      {s.watch_started_at ? (
                                        <Tooltip title={fmtDateTime(s.watch_started_at)}>
                                          <Typography variant="caption" sx={{ color: 'info.main' }}>
                                            {formatRelativeTime(s.watch_started_at)}
                                          </Typography>
                                        </Tooltip>
                                      ) : (
                                        <Typography variant="caption" color="text.disabled">—</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                      {s.watch_finished_at ? (
                                        <Tooltip title={fmtDateTime(s.watch_finished_at)}>
                                          <Typography variant="caption" sx={{ color: 'success.main' }}>
                                            {formatRelativeTime(s.watch_finished_at)}
                                          </Typography>
                                        </Tooltip>
                                      ) : (
                                        <Typography variant="caption" color="text.disabled">—</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                      {!s.revoked_at ? (
                                        <input type="date"
                                          defaultValue={s.expires_at ? new Date(s.expires_at).toISOString().split('T')[0] : ''}
                                          style={{ background: 'transparent', border: '1px solid rgba(232,230,227,0.15)', borderRadius: 4, color: 'inherit', fontSize: '0.75rem', padding: '2px 4px', cursor: 'pointer' }}
                                          onChange={async (e) => {
                                            if (!e.target.value) return;
                                            try {
                                              await api.patch(`/api/delivery/shares/${s.id}/expires`, { expires_at: new Date(e.target.value + 'T23:59:59Z').toISOString() });
                                              handleRefresh();
                                            } catch (_) { /* swallow — best-effort */ }
                                          }}
                                        />
                                      ) : (
                                        <Typography variant="caption">{s.expires_at ? fmtDate(s.expires_at) : '—'}</Typography>
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                      {(() => {
                                        // Three-state: Revoked > Expired > Active.
                                        // A share whose window has closed is no
                                        // longer usable even if not revoked, so
                                        // surface "Expired" so operators don't
                                        // assume "Active = recipient can still
                                        // open it."
                                        if (s.revoked_at) {
                                          return <Chip size="small" label="Revoked" color="default" variant="outlined" />;
                                        }
                                        const expired = s.expires_at && new Date(s.expires_at).getTime() < Date.now();
                                        if (expired) {
                                          return <Chip size="small" label="Expired" color="warning" variant="outlined" />;
                                        }
                                        return <Chip size="small" label="Active" color="success" variant="outlined" />;
                                      })()}
                                      {!s.revoked_at && (() => {
                                        // Recipient lifecycle chip: answers "did
                                        // they even click the email link?" at a
                                        // glance. Backend computes the furthest
                                        // stage; we just render a colored chip.
                                        const stageColor = {
                                          not_sent:            'default',
                                          sent:                'default',
                                          opened:              'info',
                                          nda_accepted:        'info',
                                          download_started:    'warning',
                                          streaming_started:   'warning',
                                          download_completed:  'success',
                                          streaming_finished:  'success',
                                        }[s.activity_stage] || 'default';
                                        if (!s.activity_label) return null;
                                        return (
                                          <Chip
                                            size="small"
                                            label={s.activity_label}
                                            color={stageColor}
                                            variant="outlined"
                                            sx={{ ml: 0.5 }}
                                          />
                                        );
                                      })()}
                                      {/* v10.21.33: distinct watch sittings.
                                          Backend distinguishes "kept watching
                                          where I left off" from "started over"
                                          via session start position vs. the
                                          furthest point any earlier session
                                          reached on this share. Renders only
                                          when ≥ 2 — one watch is the default
                                          assumption and showing "Watched 1x"
                                          would be noise on every chip. */}
                                      {typeof s.watch_distinct_count === 'number' && s.watch_distinct_count >= 2 && (
                                        <Tooltip title="Distinct watch sittings (resumes don't count as a new watch)">
                                          <Chip
                                            size="small"
                                            label={`Watched ${s.watch_distinct_count}×`}
                                            color="info"
                                            variant="outlined"
                                            sx={{ ml: 0.5 }}
                                          />
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }} align="right">
                                      {!s.revoked_at && (
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                          <Tooltip title="Copy download link">
                                            <IconButton size="small" onClick={() => navigator.clipboard.writeText(`${CANONICAL_ORIGIN}/d/${s.token}`)}>
                                              <CopyIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          {s.recipient_email && (
                                            <Tooltip title="Resend delivery email">
                                              <IconButton size="small" onClick={async () => {
                                                try {
                                                  await api.post(`/api/delivery/shares/${s.id}/resend`);
                                                  setSnackbar({
                                                    open: true,
                                                    severity: 'success',
                                                    message: `Delivery email resent to ${s.recipient_name || s.recipient_email}`,
                                                  });
                                                  handleRefresh();
                                                } catch (e) {
                                                  setSnackbar({
                                                    open: true,
                                                    severity: 'error',
                                                    message: `Couldn't resend: ${e?.response?.data?.detail || e.message || 'unknown error'}`,
                                                  });
                                                }
                                              }}>
                                                <ResendIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          )}
                                          <Tooltip title="Revoke this link">
                                            <IconButton size="small" onClick={async () => {
                                              if (!window.confirm('Revoke this download link?')) return;
                                              await dispatch(revokeShare(s.id)); handleRefresh();
                                            }}>
                                              <DeleteIcon fontSize="small" color="error" />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      )}
                                      {s.revoked_at && (
                                        <Tooltip title="Delete this record">
                                          <IconButton size="small" onClick={async () => {
                                            try { await api.delete(`/api/delivery/shares/${s.id}`); handleRefresh(); } catch (_) { /* swallow — best-effort */ }
                                          }}>
                                            <DeleteIcon fontSize="small" color="error" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>

                            <ReviewCommentsPanel
                              jobId={job.id}
                              jobAllowReviews={!!job.allow_reviews}
                              onChange={() => {
                                // After mark-seen / delete, refresh the
                                // top-level jobs list so the unread badge
                                // count on the row updates immediately.
                                dispatch(fetchJobs());
                              }}
                            />

                            <FrameCaptureAuditPanel
                              jobId={job.id}
                              jobAllowFrameCapture={!!job.allow_frame_capture}
                            />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {deliverJob && (
        <DeliverDialog
          open={!!deliverJob}
          onClose={() => { setDeliverJob(null); handleRefresh(); }}
          sourceType="screener"
          sourceJobId={deliverJob.id}
          projectName={deliverJob.source_filename?.replace(/\.[^/.]+$/, '') || 'Watermark Encode'}
          filename={deliverJob.output_filename || deliverJob.source_filename}
          recipientName={deliverJob.recipient_name}
          recipientEmail={deliverJob.recipient_email}
        />
      )}

      <Drawer
        anchor="right"
        open={!!analyticsJob}
        onClose={() => setAnalyticsJob(null)}
        PaperProps={{ sx: {
          width: { xs: '100%', sm: '90vw', md: 880, lg: 1100, xl: 1240 },
          maxWidth: '95vw',
        } }}
      >
        {analyticsJob && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Watch analytics</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {analyticsJob.recipient_name} — {analyticsJob.source_filename}
            </Typography>
            {analyticsLoading && <Typography variant="body2">Loading…</Typography>}
            {analyticsData?.error && <Alert severity="error">{analyticsData.error}</Alert>}
            {analyticsData && !analyticsData.error && (
              <AnalyticsContent data={analyticsData} job={analyticsJob} onReload={openAnalytics} />
            )}
          </Box>
        )}
      </Drawer>

      <Dialog open={!!bulkAction} onClose={() => setBulkAction(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {bulkAction?.kind === 'archive'
            ? `Archive ${selectedJobIds.size} encodes?`
            : bulkAction?.kind === 'revoke'
              ? `Revoke shares on ${selectedJobIds.size} encodes?`
              : `Resend delivery emails on ${selectedJobIds.size} encodes?`}
        </DialogTitle>
        <DialogContent>
          {bulkAction?.kind === 'archive' && (
            <Typography variant="body2">
              Archive removes the encoded file from disk but preserves the
              job rows + metrics (watch sessions, downloads, comments, audit)
              for reporting. Recoverable via "Show archived."
            </Typography>
          )}
          {bulkAction?.kind === 'revoke' && (
            <Typography variant="body2">
              Revokes every active share on the selected encodes. Recipients
              will get HTTP 410 if they try the link. Cannot be undone.
            </Typography>
          )}
          {bulkAction?.kind === 'resend' && (
            <Typography variant="body2">
              Re-sends the delivery email to every active share on the
              selected encodes. Useful to bump stalled deliveries. Recipients
              will see the same link they already had.
            </Typography>
          )}
          <Box sx={{ mt: 2, p: 1.5, maxHeight: 200, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {Array.from(selectedJobIds).slice(0, 50).map((id) => {
              const j = (jobs || []).find((x) => x.id === id);
              if (!j) return null;
              return (
                <Typography key={id} variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                  {j.source_filename}{j.recipient_name ? ` → ${j.recipient_name}` : ''}
                </Typography>
              );
            })}
            {selectedJobIds.size > 50 && (
              <Typography variant="caption" color="text.secondary">
                …and {selectedJobIds.size - 50} more
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkAction(null)} disabled={bulk.busy}>Cancel</Button>
          <Button
            color={bulkAction?.kind === 'archive' ? 'error' : bulkAction?.kind === 'revoke' ? 'warning' : 'primary'}
            variant="contained"
            disabled={bulk.busy}
            onClick={() => {
              if (bulkAction?.kind === 'archive') doBulkArchive();
              else if (bulkAction?.kind === 'revoke') doBulkRevoke();
              else if (bulkAction?.kind === 'resend') doBulkResend();
            }}
          >
            {bulkAction?.kind === 'archive' ? 'Archive' : bulkAction?.kind === 'revoke' ? 'Revoke' : 'Resend'} {selectedJobIds.size}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Archive watermark encode?</DialogTitle>
        <DialogContent>
          <Typography>Archive <strong>{deleteConfirm?.source_filename}</strong>?</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            The encoded file is removed from disk, but the job row + metrics
            (watch sessions, downloads, comments, audit) are preserved for
            reporting. You can restore later from the archive view ("Show
            archived" toggle), though the encoded file would need to be
            re-created.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Archive</Button>
        </DialogActions>
      </Dialog>

      <DownloadGateDialog
        job={downloadGateJob}
        onClose={() => setDownloadGateJob(null)}
        onSave={async (payload) => {
          // .unwrap() so a rejected thunk actually throws — otherwise the
          // dialog closes on backend errors and the operator never sees
          // why the save didn't stick.
          await dispatch(updateJobDownload({ jobId: downloadGateJob.id, ...payload })).unwrap();
          setDownloadGateJob(null);
          handleRefresh();
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
